// Expanded mock data with comprehensive educational content

import { Lesson } from './courseService';

// Course 1: Introduction to Machine Learning (6 lessons)
export const INTRO_ML_LESSONS: Lesson[] = [
  {
    id: '1-1',
    course_id: '1',
    title: 'What is Machine Learning?',
    description: 'Discover what machine learning is and how it\'s different from traditional programming.',
    content: `# What is Machine Learning?

Imagine you're teaching a child to recognize cats in photos. In traditional programming, you would need to write detailed rules: "Look for pointy ears, whiskers, four legs, a tail..." But what if the cat is sleeping and you can't see its legs? Or it's a hairless breed without visible whiskers?

**Machine Learning takes a different approach.** Instead of writing rules, we show the computer thousands of photos labeled "cat" or "not cat" and let it figure out the patterns on its own.

## Traditional Programming vs Machine Learning

**Traditional Programming:**
- 📝 We write explicit rules and logic
- 🔄 Input + Rules = Output
- 🧠 Human intelligence defines every step
- ⚠️ Struggles with complex, changing patterns

**Machine Learning:**
- 📊 We provide examples (data)
- 🤖 Input + Data = Program that produces Output
- 📈 Computer discovers patterns automatically
- 🌟 Excels at complex, nuanced problems

## Real-World Analogy

Think of learning to drive:
- **Traditional approach**: Memorizing every possible rule ("If red light, stop. If green light, go. If pedestrian at 2 o'clock moving at 3 mph...")
- **ML approach**: Practice with an experienced driver, gradually learning from examples until you can handle new situations intuitively

## Why is ML Powerful?

1. **Handles Complexity**: Can find patterns humans might miss
2. **Scales Automatically**: Works with millions of examples  
3. **Adapts to Change**: Can update as new data comes in
4. **Works with Uncertainty**: Makes probabilistic decisions

Machine learning isn't magic—it's a powerful tool that learns from experience, just like humans do, but at a scale and speed we can't match.`,
    lesson_type: 'text',
    order_index: 1,
    estimated_duration_minutes: 15,
    video_url: null,
    resources: {},
    is_preview: true,
    is_required: true,
    points_value: 10,
    created_at: '2024-01-10T09:30:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '1-2',
    course_id: '1',
    title: 'Types of Machine Learning',
    description: 'Explore the three main types of machine learning: supervised, unsupervised, and reinforcement learning.',
    content: `# Types of Machine Learning

Machine learning isn't one-size-fits-all. Different problems need different approaches. Let's explore the three main types:

## 🎯 Supervised Learning
**"Learning with a Teacher"**

You have input data AND the correct answers (labels). Like studying for a test with an answer key.

**Examples:**
- Email spam detection (emails labeled as "spam" or "not spam")
- Medical diagnosis (symptoms → disease labels)  
- Stock price prediction (historical data → future prices)
- Image classification (photos → "cat", "dog", "bird")

**Think of it like:** Learning math with a textbook that shows you both the problems AND solutions.

## 🔍 Unsupervised Learning
**"Learning without Labels"**

You only have input data but no "correct answers." The algorithm finds hidden patterns on its own.

**Examples:**
- Customer segmentation (grouping customers by behavior)
- Recommendation systems (finding similar preferences)
- Fraud detection (spotting unusual patterns)
- Gene sequencing (finding genetic similarities)

**Think of it like:** Organizing a messy photo album without knowing what the pictures are—you group similar-looking ones together.

## 🎮 Reinforcement Learning
**"Learning through Trial and Error"**

The algorithm learns by trying actions and getting rewards or penalties. Like training a pet with treats.

**Examples:**
- Game playing (chess, Go, video games)
- Robot navigation (learning to walk or drive)
- Trading algorithms (buy/sell decisions)
- Chatbot conversations (learning better responses)

**Think of it like:** Learning to ride a bike—you try different things, crash a few times, but gradually get better through practice.

## Which Type to Use?

- **Got labeled examples?** → Supervised Learning
- **Looking for hidden patterns?** → Unsupervised Learning  
- **Need to make sequential decisions?** → Reinforcement Learning

Most real-world applications combine multiple approaches!`,
    lesson_type: 'interactive',
    order_index: 2,
    estimated_duration_minutes: 20,
    video_url: null,
    resources: {
      interactiveType: 'drag-drop-classification',
      scenarios: [
        { text: 'Predicting house prices from size, location, and age', answer: 'supervised', explanation: 'We have input features and known sale prices (labels)' },
        { text: 'Grouping customers who buy similar products', answer: 'unsupervised', explanation: 'No predefined groups - we are discovering patterns' },
        { text: 'Teaching a robot to play ping pong', answer: 'reinforcement', explanation: 'The robot learns through trial and error, getting rewards for successful hits' },
        { text: 'Detecting credit card fraud from transaction patterns', answer: 'unsupervised', explanation: 'We are looking for unusual patterns without labeled "fraud" examples' },
        { text: 'Translating English text to French', answer: 'supervised', explanation: 'We train on pairs of English and French sentences' },
        { text: 'Optimizing delivery routes for packages', answer: 'reinforcement', explanation: 'The algorithm tries different routes and learns from efficiency feedback' }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 15,
    created_at: '2024-01-10T10:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '1-3',
    course_id: '1',
    title: 'ML in the Real World',
    description: 'See how machine learning is used in everyday applications you already know.',
    content: `# Machine Learning in the Real World

You've been using machine learning dozens of times today—you just didn't realize it! Let's explore how ML powers the apps and services you use every day.

## 🎬 Netflix: "What Should I Watch Tonight?"

**The Challenge:** With 15,000+ titles, how does Netflix know what you'll love?

**ML Solution:** 
- Tracks what you watch, pause, rewind, or skip
- Analyzes viewing patterns of millions of similar users
- Considers time of day, device, and even how long you scroll
- Creates personalized recommendations just for you

**Fun Fact:** Netflix saves $1 billion annually by keeping subscribers engaged through better recommendations!

## 🎵 Spotify: Your Personal DJ

**The Magic:** Discover Weekly playlists that feel hand-curated

**How It Works:**
- **Collaborative Filtering:** "People who like Artist A also enjoy Artist B"
- **Content Analysis:** Analyzes audio features (tempo, key, energy)
- **Natural Language Processing:** Reads music blogs and reviews
- **Real-time Learning:** Adapts as your taste evolves

**Result:** 40+ billion hours of personalized music per year

## 🚗 Self-Driving Cars: The Future is Here

**The Vision:** Cars that drive safer than humans

**ML Components:**
- **Computer Vision:** Recognizes pedestrians, signs, lane markings
- **Sensor Fusion:** Combines cameras, radar, lidar data
- **Path Planning:** Predicts optimal routes in real-time
- **Reinforcement Learning:** Improves from millions of driving scenarios

**Current Reality:** Tesla's Autopilot has driven over 3 billion miles, learning from each one.

## 🏥 Medical Diagnosis: AI Doctors

**Breakthrough Applications:**
- **Radiology:** AI detects cancer in mammograms more accurately than human radiologists
- **Ophthalmology:** Google's AI diagnoses diabetic retinopathy from eye photos
- **Drug Discovery:** AI finds new medications 100x faster than traditional methods
- **Surgery:** Robotic assistants guide precision operations

**Impact:** Early detection saves lives and reduces healthcare costs globally.

## 📱 Your Phone's Hidden AI

**Every Day, Your Phone Uses ML For:**
- 📸 Camera: Auto-focus, scene detection, portrait mode
- 🗣️ Voice: Siri/Google Assistant speech recognition
- 📝 Keyboard: Autocorrect and predictive text
- 🔋 Battery: Optimizing power usage based on your habits
- 🔒 Security: Face ID and fingerprint recognition
- 📧 Email: Spam filtering and smart replies

## 🛒 E-commerce: The Perfect Shopping Assistant

**Amazon's ML Arsenal:**
- **Product Recommendations:** "Customers who bought this also bought..."
- **Price Optimization:** Dynamic pricing based on demand
- **Inventory Management:** Predicting what to stock where
- **Fraud Detection:** Spotting suspicious transactions instantly
- **Alexa:** Voice shopping and smart home control

## 🌐 Social Media: The Algorithm Behind Your Feed

**How Facebook/Instagram/TikTok Work:**
- Analyzes your likes, shares, comments, and scroll time
- Considers your relationships and interactions
- Predicts what content keeps you engaged longest
- Balances friends' posts with ads and suggested content
- Constantly experiments to optimize engagement

## The Bottom Line

Machine learning isn't futuristic—it's here, now, making your daily life more convenient, efficient, and personalized. Every click, scroll, and swipe generates data that makes these systems smarter.

**What's Next?** As ML continues evolving, we'll see even more seamless integration into healthcare, education, transportation, and entertainment. The question isn't whether AI will change your life—it's how much more it will improve it.`,
    lesson_type: 'text',
    order_index: 3,
    estimated_duration_minutes: 18,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: true,
    points_value: 12,
    created_at: '2024-01-10T11:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '1-4',
    course_id: '1',
    title: 'The ML Workflow',
    description: 'Learn the step-by-step process of building a machine learning solution.',
    content: `# The Machine Learning Workflow

Every successful ML project follows a similar roadmap. Think of it like cooking a gourmet meal—there are essential steps you can't skip, and the order matters!

## 🎯 Step 1: Define the Problem

**Before touching any data, ask:**
- What specific problem am I trying to solve?
- How will I measure success?
- What type of ML problem is this? (Classification? Prediction? Clustering?)

**Example:** "I want to predict whether an email is spam with 95%+ accuracy"

## 📊 Step 2: Collect Data

**Data is your raw ingredient—quality matters more than quantity**

**Sources:**
- 💾 Company databases
- 🌐 Web scraping  
- 📱 APIs (Twitter, Instagram, etc.)
- 🔬 Surveys and experiments
- 🏪 Public datasets (Kaggle, government data)

**Rule of Thumb:** More diverse, high-quality data beats massive amounts of poor data

## 🧹 Step 3: Clean & Prepare Data

**Real data is messy!** This step takes 60-80% of project time.

**Common Issues:**
- ❌ Missing values ("Customer didn't fill out age")
- 🔁 Duplicate records
- 📏 Different scales (age in years vs income in dollars)
- 🏷️ Inconsistent labels ("Yes/Y/True" all meaning the same thing)
- 🚫 Outliers (person claiming to be 250 years old)

**Goal:** Clean, consistent data your algorithm can understand

## 🔬 Step 4: Explore & Visualize

**Become a data detective!**
- 📈 Create charts and graphs
- 🔍 Look for patterns and trends
- ❓ Ask questions: "Why do sales spike in December?"
- 🧮 Calculate statistics (averages, ranges, correlations)

**This step guides your modeling decisions**

## 🤖 Step 5: Choose & Train Model

**Select the right tool for the job:**
- 🌳 Decision Trees (easy to understand)
- 📊 Linear Regression (simple predictions)
- 🧠 Neural Networks (complex patterns)
- 🎯 Support Vector Machines (classification)

**Training = Showing the model examples until it learns patterns**

## 📏 Step 6: Evaluate Performance

**How good is your model really?**

**Key Questions:**
- How accurate are predictions on new, unseen data?
- Does it work equally well for all groups?
- Are there patterns in its mistakes?

**Common Metrics:**
- 🎯 Accuracy: What % of predictions are correct?
- ⚖️ Precision: Of positive predictions, how many are actually correct?
- 📊 Recall: Of all actual positives, how many did we catch?

## 🚀 Step 7: Deploy & Monitor

**Making your model useful in the real world**

**Deployment Options:**
- 🌐 Web application
- 📱 Mobile app integration
- ⚙️ Automated system
- 🔌 API for other services

**Ongoing Monitoring:**
- 📈 Track performance over time
- 🔄 Retrain with new data
- 🐛 Fix issues as they arise
- 📊 A/B test improvements

## 🔄 Step 8: Iterate & Improve

**ML is not "set and forget"—it's a continuous cycle**

Based on real-world performance:
- 🔧 Tune model parameters
- 🆕 Add new features
- 📊 Collect more/better data
- 🔄 Try different algorithms
- 🎯 Refine the problem definition

## Real Example: Spam Email Detection

1. **Problem:** Classify emails as spam/not spam
2. **Data:** Collect 100k labeled emails
3. **Clean:** Remove duplicates, handle missing data
4. **Explore:** Find common spam words, sender patterns
5. **Model:** Train on email text features
6. **Evaluate:** Test on 20k new emails → 98% accuracy
7. **Deploy:** Integrate into email client
8. **Monitor:** Track false positives, retrain monthly

## Key Takeaways

✅ **Each step builds on the previous**  
✅ **Iteration is normal—rarely perfect on first try**  
✅ **Data quality is more important than model complexity**  
✅ **Domain expertise helps at every step**  
✅ **Success is measured by real-world impact, not just accuracy**

The workflow might seem linear, but in practice, you'll jump between steps as you discover new insights!`,
    lesson_type: 'interactive',
    order_index: 4,
    estimated_duration_minutes: 25,
    video_url: null,
    resources: {
      interactiveType: 'drag-sequence',
      steps: [
        'Define the Problem',
        'Collect Data',
        'Clean & Prepare Data',
        'Explore & Visualize',
        'Choose & Train Model',
        'Evaluate Performance',
        'Deploy & Monitor',
        'Iterate & Improve'
      ],
      scenarios: [
        {
          title: 'Building a Movie Recommendation System',
          correctOrder: [1, 2, 3, 4, 5, 6, 7, 8],
          description: 'Help a streaming service recommend movies to users'
        }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 20,
    created_at: '2024-01-10T12:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '1-5',
    course_id: '1',
    title: 'Key ML Terms',
    description: 'Master the essential vocabulary of machine learning.',
    content: `# Key Machine Learning Terms

Like any field, machine learning has its own vocabulary. Don't worry—once you understand these core terms, you'll sound like a pro!

## 📊 Data & Features

**Dataset:** Your collection of examples (like a spreadsheet)
**Features:** Individual pieces of information about each example (columns in your spreadsheet)
**Labels:** The "correct answer" you're trying to predict
**Training Data:** Examples used to teach the algorithm
**Test Data:** Fresh examples used to check performance

## 🤖 Algorithms & Models

**Algorithm:** The recipe/method for learning (like "Decision Tree")
**Model:** The specific result after training (your trained Decision Tree)
**Parameters:** Internal settings the model learns
**Hyperparameters:** Settings you choose before training

## 📈 Performance & Evaluation

**Accuracy:** Percentage of correct predictions
**Overfitting:** Model memorizes training data but fails on new data
**Underfitting:** Model is too simple to learn meaningful patterns
**Cross-validation:** Testing technique to get reliable performance estimates

## 🎯 Types of Problems

**Classification:** Predicting categories (spam/not spam, cat/dog)
**Regression:** Predicting numbers (price, temperature, age)
**Clustering:** Grouping similar things together
**Dimensionality Reduction:** Simplifying complex data

Let's test your understanding with a quick quiz!`,
    lesson_type: 'quiz',
    order_index: 5,
    estimated_duration_minutes: 12,
    video_url: null,
    resources: {
      questions: [
        {
          question: "What is 'overfitting' in machine learning?",
          options: [
            "When a model works perfectly on training data but poorly on new data",
            "When a model uses too many features",
            "When a model is too big for your computer",
            "When a model takes too long to train"
          ],
          correctAnswer: 0,
          explanation: "Overfitting occurs when a model memorizes the training data instead of learning general patterns. It's like a student who memorizes answers for a practice test but can't handle new questions."
        },
        {
          question: "In a house price prediction problem, what would be a 'feature'?",
          options: [
            "The final predicted price",
            "The number of bedrooms",
            "The machine learning algorithm used",
            "The accuracy of the model"
          ],
          correctAnswer: 1,
          explanation: "Features are the input variables used to make predictions. Number of bedrooms is information about the house that helps predict its price."
        },
        {
          question: "What type of ML problem is email spam detection?",
          options: [
            "Regression",
            "Clustering", 
            "Classification",
            "Reinforcement Learning"
          ],
          correctAnswer: 2,
          explanation: "Spam detection is classification because we're categorizing emails into discrete groups: 'spam' or 'not spam'."
        },
        {
          question: "What is the difference between an algorithm and a model?",
          options: [
            "There is no difference - they're the same thing",
            "Algorithm is the method; model is the result after training",
            "Models are more accurate than algorithms",
            "Algorithms are newer than models"
          ],
          correctAnswer: 1,
          explanation: "Think of an algorithm as a recipe, and a model as the specific cake you baked using that recipe. The algorithm is the general method; the model is the trained result."
        },
        {
          question: "Why do we separate data into training and test sets?",
          options: [
            "To make the dataset smaller and easier to work with",
            "To check if our model can perform well on data it hasn't seen before",
            "To speed up the training process",
            "To reduce overfitting during training"
          ],
          correctAnswer: 1,
          explanation: "We need test data to honestly evaluate how our model performs on new, unseen examples. Training and testing on the same data would be like grading a student's homework using the same problems they studied!"
        }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 15,
    created_at: '2024-01-10T13:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  },
  {
    id: '1-6',
    course_id: '1',
    title: 'Your Learning Path',
    description: 'Plan your next steps in machine learning education.',
    content: `# Your Learning Path Forward

Congratulations! 🎉 You've completed your introduction to machine learning. You now understand what ML is, how it works, and where it's used. But this is just the beginning of an exciting journey.

## 🎯 What You've Accomplished

✅ **Understanding**: You know what machine learning is and isn't  
✅ **Types**: You can distinguish supervised, unsupervised, and reinforcement learning  
✅ **Applications**: You recognize ML in everyday apps and services  
✅ **Workflow**: You understand the end-to-end process of ML projects  
✅ **Vocabulary**: You speak the language of machine learning

## 🗺️ Your Next Steps

### Option 1: Build Your First Model 🔨
**Take "Your First ML Model"** - Get hands-on experience building a real prediction system
- **Best for:** People who learn by doing
- **You'll build:** A movie recommendation system
- **Skills:** Data preparation, model training, evaluation

### Option 2: Master the Data 📊  
**Take "Data Fundamentals for ML"** - Become an expert at working with data
- **Best for:** People interested in data analysis
- **You'll learn:** Finding, cleaning, and visualizing data
- **Skills:** Excel/Python for data, statistical thinking

### Option 3: Specialize Early 🎯
Choose a specific application area:
- **Computer Vision**: Image recognition, medical imaging
- **Natural Language Processing**: Chatbots, translation
- **Time Series**: Stock predictions, sales forecasting
- **Recommender Systems**: Netflix, Spotify, e-commerce

## 📚 Learning Resources Beyond This Platform

### 📖 Books for Beginners
- **"Hands-On Machine Learning"** by Aurélien Géron
- **"Pattern Recognition and Machine Learning"** by Christopher Bishop
- **"The Elements of Statistical Learning"** (free PDF available)

### 💻 Coding Platforms
- **Kaggle Learn**: Free micro-courses
- **Google Colab**: Free environment for Python coding
- **GitHub**: Explore real ML projects

### 🎓 University Courses (Free!)
- **Andrew Ng's ML Course** (Coursera/YouTube)
- **MIT 6.034 Artificial Intelligence** (YouTube)
- **Stanford CS229** (YouTube)

## 🔧 Tools to Explore

### Programming Languages
- **Python** 🐍: Most popular for ML (start here!)
- **R** 📊: Great for statistics and data analysis
- **JavaScript** 🌐: ML in web browsers
- **SQL** 🗄️: Essential for working with databases

### Key Libraries (Python)
- **pandas**: Data manipulation
- **scikit-learn**: Machine learning algorithms
- **matplotlib/seaborn**: Data visualization
- **TensorFlow/PyTorch**: Deep learning

## 🏗️ Project Ideas to Try

### Beginner Projects
1. **Predict Housing Prices** using neighborhood data
2. **Classify Movie Reviews** as positive/negative
3. **Recommend Books** based on reading history
4. **Predict Sports Outcomes** using team statistics

### Intermediate Projects  
1. **Build a Chatbot** for customer service
2. **Create an Image Classifier** for your photo collection
3. **Stock Price Prediction** using historical data
4. **Fraud Detection System** for transactions

## 🌟 Career Paths in ML

### Popular Roles
- **Data Scientist**: Extract insights from data
- **ML Engineer**: Build and deploy ML systems  
- **Research Scientist**: Develop new algorithms
- **Product Manager**: Guide ML product development
- **Data Analyst**: Understand business through data

### Industries Using ML
- 💰 **Finance**: Fraud detection, trading, credit scoring
- 🏥 **Healthcare**: Diagnosis, drug discovery, personalized treatment
- 🚗 **Transportation**: Self-driving cars, route optimization
- 🛒 **E-commerce**: Recommendations, pricing, inventory
- 🎮 **Entertainment**: Content recommendations, game AI

## 💡 Tips for Success

### 1. Practice Regularly
- Code a little bit every day
- Work on projects that interest you
- Join online communities (Reddit r/MachineLearning, Kaggle)

### 2. Focus on Fundamentals
- Statistics and probability
- Linear algebra basics
- Programming fundamentals
- Critical thinking about data

### 3. Build a Portfolio
- Create GitHub repositories with your projects
- Write blog posts explaining what you learned
- Participate in Kaggle competitions
- Contribute to open source projects

### 4. Stay Curious
- Follow ML researchers on Twitter
- Read papers (start with abstracts)
- Try new tools and techniques
- Question everything—including your own assumptions

## 🚀 Your Assignment

Before moving on, choose ONE next step:

1. **Enroll in "Your First ML Model"** if you want hands-on experience
2. **Enroll in "Data Fundamentals"** if you want to master data skills
3. **Start a personal project** using the ideas above
4. **Join an online ML community** to connect with other learners

## Remember...

Machine learning is a journey, not a destination. Every expert was once a beginner. Stay curious, be patient with yourself, and celebrate small wins along the way.

The future belongs to those who can work alongside intelligent systems. By learning ML, you're not just gaining a valuable skill—you're preparing to shape the future.

**Welcome to the world of machine learning!** 🌟

*Your adventure is just beginning...*`,
    lesson_type: 'text',
    order_index: 6,
    estimated_duration_minutes: 10,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: false,
    points_value: 8,
    created_at: '2024-01-10T14:00:00Z',
    updated_at: '2024-03-15T14:30:00Z'
  }
];

// Course 2: Your First ML Model (8 lessons)
export const FIRST_MODEL_LESSONS: Lesson[] = [
  {
    id: '2-1',
    course_id: '2',
    title: 'Setting Up Your Environment',
    description: 'Get ready to build your first machine learning model with the right tools.',
    content: `# Setting Up Your ML Environment

Before building your first model, let's set up the perfect workspace. Think of this like organizing your kitchen before cooking a complex meal!

## 🐍 Why Python for Machine Learning?

Python dominates ML for good reasons:
- **Simple syntax**: Reads almost like English
- **Rich libraries**: Pre-built tools for everything
- **Huge community**: Help is always available
- **Industry standard**: Used by Google, Netflix, Uber

## 🛠️ Essential Tools We'll Use

### 1. Google Colab (Recommended for Beginners)
**What it is:** Free online Python environment
**Why it's perfect:**
- ✅ No installation required
- ✅ Pre-installed ML libraries
- ✅ Free GPU access
- ✅ Easy sharing and collaboration

**Getting Started:**
1. Go to colab.research.google.com
2. Sign in with Google account
3. Click "New Notebook"
4. You're ready to code!

### 2. Local Setup (For Advanced Users)
If you prefer working offline:
- **Anaconda**: Python + essential libraries in one package
- **Jupyter Notebook**: Interactive coding environment
- **VS Code**: Professional code editor

## 📚 Key Libraries in Our Toolkit

### pandas 🐼
**Purpose:** Data manipulation and analysis
\`\`\`python
import pandas as pd
# Load data from CSV, Excel, databases
# Clean and transform data
# Explore with statistics and charts
\`\`\`

### scikit-learn 🔬
**Purpose:** Machine learning algorithms
\`\`\`python
from sklearn.linear_model import LinearRegression
# Train models with simple commands
# Evaluate performance easily
# Dozens of algorithms ready to use
\`\`\`

### matplotlib & seaborn 📊
**Purpose:** Data visualization
\`\`\`python
import matplotlib.pyplot as plt
import seaborn as sns
# Create beautiful charts and graphs
# Understand your data visually
\`\`\`

### numpy 🔢
**Purpose:** Numerical computing
\`\`\`python
import numpy as np
# Fast mathematical operations
# Foundation for other libraries
\`\`\`

## 🏃‍♀️ Quick Setup Verification

Let's make sure everything works! Try this code:

\`\`\`python
# Import essential libraries
import pandas as pd
import numpy as np
import matplotlib.pyplot as plt
from sklearn.linear_model import LinearRegression
from sklearn.model_selection import train_test_split

# Create sample data
data = {'size': [1200, 1500, 1800, 2100, 2400],
        'price': [200000, 250000, 300000, 350000, 400000]}
df = pd.DataFrame(data)

# Quick visualization
plt.scatter(df['size'], df['price'])
plt.xlabel('House Size (sq ft)')
plt.ylabel('Price ($)')
plt.title('House Prices vs Size')
plt.show()

print("🎉 Setup successful! Ready to build models!")
\`\`\`

## 🎯 What You'll Build in This Course

**Project: Movie Rating Predictor**
- Predict movie ratings based on features like genre, director, budget
- Use real movie dataset
- Learn complete ML workflow
- Deploy your model as a web app

## 📝 Best Practices From Day One

### 1. Organized File Structure
\`\`\`
my-ml-project/
├── data/              # Raw and processed datasets
├── notebooks/         # Jupyter notebooks for exploration
├── src/              # Clean Python scripts
├── models/           # Saved trained models
└── README.md         # Project description
\`\`\`

### 2. Document Everything
- Write comments explaining your thinking
- Include markdown cells in notebooks
- Document your data sources
- Note what worked and what didn't

### 3. Version Control
- Use Git to track changes
- Commit early and often
- Create meaningful commit messages
- Back up your work on GitHub

## 🚨 Common Beginner Mistakes to Avoid

❌ **Not checking data quality first**
✅ Always explore and clean data before modeling

❌ **Using entire dataset for training**
✅ Always split data into train/test sets

❌ **Ignoring missing values**
✅ Handle missing data explicitly

❌ **Not validating results**
✅ Always test models on unseen data

❌ **Overcomplicating early models**
✅ Start simple, then add complexity

## 🎪 Fun Fact

The first "machine learning" was actually done by hand! In the 1950s, researchers manually calculated statistics and patterns. Today, your laptop has more computing power than entire research institutions from that era!

## ✅ Environment Checklist

Before proceeding, make sure you can:
- [ ] Access Google Colab or have Python installed locally
- [ ] Import pandas, numpy, sklearn, and matplotlib without errors
- [ ] Create and run a simple Python script
- [ ] Save and organize your work

**Ready?** Let's build something amazing! 🚀

*In our next lesson, we'll explore real data and discover the patterns hidden within...*`,
    lesson_type: 'text',
    order_index: 1,
    estimated_duration_minutes: 20,
    video_url: null,
    resources: {},
    is_preview: true,
    is_required: true,
    points_value: 12,
    created_at: '2024-01-20T11:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  // Additional lessons for Course 2...
  {
    id: '2-2',
    course_id: '2',
    title: 'Understanding Your Data',
    description: 'Explore a sample dataset and learn to identify patterns that machines can learn from.',
    content: `# Understanding Your Data

Data is the fuel of machine learning. Before building any model, you need to become intimately familiar with your data. Let's explore our movie dataset together!

## 🎬 Meet Our Dataset: Movie Ratings

We'll use a simplified movie dataset with these features:
- **Title**: Movie name
- **Genre**: Comedy, Drama, Action, etc.
- **Budget**: Production cost (millions)
- **Runtime**: Length in minutes
- **Director_Experience**: Years directing
- **Star_Rating**: Target variable (1-10 scale)

## 🔍 First Look: Data Exploration

### Loading and Inspecting Data
\`\`\`python
import pandas as pd
import numpy as np
import matplotlib.pyplot as plt
import seaborn as sns

# Load our movie dataset
movies = pd.read_csv('movie_ratings.csv')

# Basic info about our dataset
print("Dataset Shape:", movies.shape)
print("\\nFirst 5 rows:")
print(movies.head())

print("\\nData Types:")
print(movies.dtypes)

print("\\nBasic Statistics:")
print(movies.describe())
\`\`\`

### What to Look For:
1. **Size**: How many movies? How many features?
2. **Missing Values**: Are there gaps in the data?
3. **Data Types**: Numbers? Text? Categories?
4. **Ranges**: What's the min/max of each column?

## 📊 Visualizing Patterns

### 1. Distribution of Ratings
\`\`\`python
plt.figure(figsize=(10, 6))
plt.subplot(1, 2, 1)
plt.hist(movies['Star_Rating'], bins=20, edgecolor='black')
plt.title('Distribution of Movie Ratings')
plt.xlabel('Star Rating')
plt.ylabel('Number of Movies')

plt.subplot(1, 2, 2)
sns.boxplot(y=movies['Star_Rating'])
plt.title('Rating Distribution (Box Plot)')
plt.show()
\`\`\`

**What This Tells Us:**
- Are ratings normally distributed or skewed?
- Are there outliers (unusually high/low ratings)?
- What's the typical rating range?

### 2. Budget vs Rating
\`\`\`python
plt.figure(figsize=(8, 6))
plt.scatter(movies['Budget'], movies['Star_Rating'], alpha=0.6)
plt.xlabel('Budget (Millions $)')
plt.ylabel('Star Rating')
plt.title('Movie Budget vs Rating')

# Add correlation coefficient
correlation = movies['Budget'].corr(movies['Star_Rating'])
plt.text(0.05, 0.95, f'Correlation: {correlation:.2f}', 
         transform=plt.gca().transAxes, 
         bbox=dict(boxstyle="round", facecolor='wheat'))
plt.show()
\`\`\`

**Questions to Ask:**
- Do expensive movies get better ratings?
- Is there a sweet spot for budget?
- Are there successful low-budget films?

### 3. Genre Impact
\`\`\`python
plt.figure(figsize=(12, 6))
sns.boxplot(data=movies, x='Genre', y='Star_Rating')
plt.xticks(rotation=45)
plt.title('Rating Distribution by Genre')
plt.tight_layout()
plt.show()

# Average rating by genre
genre_ratings = movies.groupby('Genre')['Star_Rating'].agg(['mean', 'count']).round(2)
print("Average Ratings by Genre:")
print(genre_ratings.sort_values('mean', ascending=False))
\`\`\`

**Insights to Discover:**
- Which genres typically rate highest?
- Are there enough movies in each genre for reliable patterns?
- Do some genres have more variable ratings?

## 🔍 Detective Work: Finding Patterns

### Correlation Matrix
\`\`\`python
# Select only numeric columns
numeric_features = ['Budget', 'Runtime', 'Director_Experience', 'Star_Rating']
correlation_matrix = movies[numeric_features].corr()

plt.figure(figsize=(8, 6))
sns.heatmap(correlation_matrix, annot=True, cmap='coolwarm', center=0)
plt.title('Feature Correlation Matrix')
plt.show()
\`\`\`

**Reading the Heatmap:**
- **+1**: Perfect positive correlation
- **0**: No linear relationship
- **-1**: Perfect negative correlation
- **Colors**: Red = positive, Blue = negative

### Interesting Questions to Explore:
1. **Runtime Sweet Spot**: Do 90-minute movies rate better than 3-hour epics?
2. **Director Experience**: Do veteran directors make better films?
3. **Budget Efficiency**: Which movies deliver the best rating per dollar?

## 🕵️‍♀️ Data Quality Investigation

### Missing Values Detective Work
\`\`\`python
# Check for missing values
missing_data = movies.isnull().sum()
print("Missing Values per Column:")
print(missing_data)

# Visualize missing data pattern
plt.figure(figsize=(10, 6))
sns.heatmap(movies.isnull(), yticklabels=False, cbar=True, cmap='viridis')
plt.title('Missing Data Pattern')
plt.show()
\`\`\`

### Outlier Detection
\`\`\`python
# Look for unusual values
print("Potential Outliers:")
print("\\nBudget outliers (>95th percentile):")
budget_threshold = movies['Budget'].quantile(0.95)
print(movies[movies['Budget'] > budget_threshold][['Title', 'Budget', 'Star_Rating']])

print("\\nRuntime outliers:")
runtime_threshold = movies['Runtime'].quantile(0.95)
print(movies[movies['Runtime'] > runtime_threshold][['Title', 'Runtime', 'Star_Rating']])
\`\`\`

## 🎯 Interactive Exercise: Data Detective

**Your Mission:** Analyze our movie dataset and answer these questions:

1. **What's the correlation between budget and rating?**
   - Strong positive, weak positive, negative, or no correlation?

2. **Which genre has the most consistent ratings?**
   - Look at the spread in box plots

3. **Do longer movies tend to rate better or worse?**
   - Check runtime vs rating correlation

4. **Name one surprising finding in the data**
   - Something that goes against common assumptions

## 🔑 Key Insights for Model Building

From our exploration, we might discover:

✅ **Strong Predictors**: Features highly correlated with ratings
✅ **Weak Predictors**: Features with little correlation
✅ **Data Issues**: Missing values, outliers, imbalances
✅ **Feature Engineering Opportunities**: New features to create

## 💡 Pro Tips for Data Exploration

### 1. Always Start with Simple Questions
- What does each row represent?
- What am I trying to predict?
- What information is available to help predict it?

### 2. Use Multiple Visualization Types
- **Histograms**: Show distributions
- **Scatter plots**: Show relationships between numbers
- **Box plots**: Compare groups
- **Correlation heatmaps**: Show all relationships at once

### 3. Think Like Your Model
- What patterns would help you predict ratings?
- Which features seem most informative?
- Are there combinations of features that might be powerful?

## 🚀 Next Steps

Now that you understand your data, you're ready to:
1. Clean and prepare the data for modeling
2. Select the most promising features
3. Build your first prediction model

**Remember:** Time spent understanding your data is never wasted. The insights you gain here will guide every decision in your ML pipeline!

*In our next lesson, we'll get our hands dirty cleaning this data and preparing it for machine learning...*`,
    lesson_type: 'interactive',
    order_index: 2,
    estimated_duration_minutes: 25,
    video_url: null,
    resources: {
      interactiveType: 'dataset-exploration',
      dataset: 'movie_ratings_sample',
      tasks: [
        'Calculate correlation between budget and rating',
        'Identify the genre with highest average rating',
        'Find movies with unusual budget-to-rating ratios',
        'Determine optimal runtime range for high ratings'
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 18,
    created_at: '2024-01-20T12:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  // Additional lessons for Course 2
  {
    id: '2-3',
    course_id: '2',
    title: 'Cleaning Your Data',
    description: 'Learn to handle messy, real-world data and prepare it for machine learning.',
    content: `# Cleaning Your Data

Real-world data is messy! Let's learn to clean it up and make it ML-ready.

## 🧹 The Data Cleaning Process

### Common Data Problems:
- Missing values
- Duplicate entries  
- Inconsistent formats
- Outliers and errors
- Inconsistent categories

### Your Cleaning Toolkit:
- pandas for data manipulation
- numpy for numerical operations
- matplotlib for visualization
- Regular expressions for text cleanup

## 💻 Hands-On Exercise

Clean this messy movie dataset:

\`\`\`python
import pandas as pd
import numpy as np

# Load messy data
movies = pd.read_csv('messy_movies.csv')

# Step 1: Check for missing values
print("Missing values:")
print(movies.isnull().sum())

# Step 2: Fill missing values
movies['budget'].fillna(movies['budget'].median(), inplace=True)
movies['rating'].fillna(movies['rating'].mean(), inplace=True)

# Step 3: Remove duplicates
movies.drop_duplicates(inplace=True)

# Step 4: Standardize text
movies['genre'] = movies['genre'].str.lower().str.strip()

print("Data cleaned successfully!")
\`\`\`

Remember: Clean data is the foundation of accurate models!`,
    lesson_type: 'coding',
    order_index: 3,
    estimated_duration_minutes: 30,
    video_url: null,
    resources: {
      starterCode: `# Movie Data Cleaning Exercise
import pandas as pd
import numpy as np

# Load the dataset
movies = pd.read_csv('messy_movies.csv')

# TODO: Handle missing values in 'budget' column
# Hint: Use fillna() with median value

# TODO: Remove duplicate rows
# Hint: Use drop_duplicates()

# TODO: Standardize genre names (lowercase, no spaces)
# Hint: Use str.lower() and str.strip()

print("Cleaning complete!")
`,
      expectedOutput: "Missing values handled\\nDuplicates removed\\nGenres standardized\\nCleaning complete!"
    },
    is_preview: false,
    is_required: true,
    points_value: 20,
    created_at: '2024-01-20T14:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '2-4',
    course_id: '2',
    title: 'Choosing a Model',
    description: 'Learn how to select the right algorithm for your machine learning problem.',
    content: `# Choosing the Right Model

With so many algorithms available, how do you pick the right one? Let's learn the art and science of model selection!

## 🎯 Start with Your Problem Type

### Classification Problems
**Goal:** Predict categories or classes
**Examples:** Spam detection, image recognition, disease diagnosis

**Top Algorithm Choices:**
- **Logistic Regression**: Simple, interpretable, fast
- **Random Forest**: Handles complex patterns, robust
- **Support Vector Machine**: Works well with limited data

### Regression Problems  
**Goal:** Predict continuous numbers
**Examples:** Price prediction, sales forecasting, risk scoring

**Top Algorithm Choices:**
- **Linear Regression**: Simple, interpretable baseline
- **Random Forest Regressor**: Handles non-linear patterns
- **Gradient Boosting**: Often wins competitions

## 🏁 The "No Free Lunch" Rule

**Key Insight:** No single algorithm works best for every problem!

**What this means:**
- The best model depends on your specific data
- Simple models often outperform complex ones
- Always try multiple approaches

## 📊 Decision Tree: Your Model Selection Guide

\`\`\`
Data Size < 1000 examples?
├─ YES → Start with Simple Models (Linear/Logistic Regression)
└─ NO → Consider Complex Models

Interpretability Important?
├─ YES → Decision Trees, Linear Models
└─ NO → Random Forest, Neural Networks

Lots of Features?
├─ YES → Regularized Models (Lasso, Ridge)
└─ NO → Standard algorithms work fine

Time Constraint?
├─ YES → Fast algorithms (Linear models, Naive Bayes)
└─ NO → Try multiple algorithms and ensemble
\`\`\`

## 🚀 The Practical Approach

### Step 1: Start Simple
- Always begin with a baseline model
- Linear/Logistic Regression is often perfect
- Understand your data before adding complexity

### Step 2: Add Complexity Gradually
- Try Random Forest (good all-around performer)
- Consider Gradient Boosting for structured data
- Neural Networks for unstructured data (images, text)

### Step 3: Validate Everything
- Use cross-validation to compare models
- Don't trust training accuracy alone
- Real-world performance is what matters

## 🎬 Movie Rating Prediction Example

**Our Problem:** Predict movie ratings (1-10) from features

**Dataset Characteristics:**
- 5,000 movies
- Mixed data types (numerical + categorical)
- Some missing values
- Target: Continuous ratings

**Model Selection Process:**
1. **Baseline:** Linear Regression (RMSE: 1.2)
2. **Improvement:** Random Forest (RMSE: 0.9) ✅
3. **Complex:** Neural Network (RMSE: 0.95)

**Winner:** Random Forest - simpler, faster, nearly as accurate!

## 💡 Pro Tips for Model Selection

### 1. Domain Knowledge Beats Fancy Algorithms
- Understand your business problem first
- Feature engineering often more important than algorithm choice
- Simple models with good features > complex models with poor features

### 2. Consider Practical Constraints
- **Latency:** Real-time predictions need fast models
- **Interpretability:** Banks/healthcare need explainable models  
- **Memory:** Mobile apps need lightweight models
- **Training Time:** Daily retraining needs efficient algorithms

### 3. Ensemble When Possible
- Combine multiple models for better performance
- Voting classifiers and averaging regressors work well
- Different algorithms capture different patterns

## 🔍 Common Beginner Mistakes

❌ **Jumping to Deep Learning First**
✅ Start with simpler algorithms, add complexity gradually

❌ **Choosing Based on Hype**  
✅ Choose based on problem characteristics and data

❌ **Not Trying Multiple Algorithms**
✅ Compare 3-5 different approaches systematically

❌ **Ignoring Computational Constraints**
✅ Consider deployment requirements from the start

## 🎪 Fun Algorithm Personalities

- **Linear Regression:** The reliable friend - simple, honest, always there
- **Random Forest:** The wise committee - asks many experts, averages opinions
- **Neural Networks:** The complex genius - can learn anything but needs lots of examples
- **SVM:** The perfectionist - finds the absolute best boundary
- **K-Means:** The organizer - groups similar things together naturally

## 🏆 Your Action Plan

1. **Define your problem type clearly**
2. **Start with a simple baseline model**
3. **Try 3-4 different algorithms systematically**
4. **Use proper validation to compare fairly**
5. **Pick the model that best fits your constraints**
6. **Remember: simple often beats complex!**

Next up: We'll implement and train your chosen model! 🚀`,
    lesson_type: 'text',
    order_index: 4,
    estimated_duration_minutes: 20,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: true,
    points_value: 15,
    created_at: '2024-01-20T15:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '2-5',
    course_id: '2',
    title: 'Training Your Model',
    description: 'Put your chosen algorithm to work and train your first machine learning model.',
    content: `# Training Your Model

Time to bring your model to life! This is where the magic happens - your algorithm learns patterns from data.

## 🎓 What is "Training" Really?

**Training = Pattern Recognition at Scale**

Think of it like teaching a child to recognize cats:
- Show thousands of cat photos (training data)
- Child gradually learns what makes a cat (pattern recognition)
- Eventually recognizes new cats they've never seen (generalization)

**In ML:**
- Algorithm examines training examples
- Finds mathematical patterns that predict outcomes
- Builds an internal "model" of these patterns
- Uses model to predict new, unseen data

## 🔧 The Training Process Step-by-Step

### Step 1: Prepare Your Data
\`\`\`python
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestRegressor
from sklearn.metrics import mean_squared_error

# Split data into features (X) and target (y)
X = movies[['budget', 'runtime', 'director_experience']]
y = movies['rating']

# Create train/test split
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42
)

print(f"Training set: {len(X_train)} examples")
print(f"Test set: {len(X_test)} examples")
\`\`\`

### Step 2: Initialize Your Model
\`\`\`python
# Create a Random Forest model
model = RandomForestRegressor(
    n_estimators=100,    # Number of trees
    random_state=42,     # For reproducible results
    max_depth=10         # Prevent overfitting
)

print("Model initialized and ready for training!")
\`\`\`

### Step 3: Train the Model
\`\`\`python
# This is where the learning happens!
model.fit(X_train, y_train)

print("🎉 Model training complete!")
print(f"Model learned from {len(X_train)} examples")
\`\`\`

### Step 4: Evaluate Performance
\`\`\`python
# Make predictions on test data
y_pred = model.predict(X_test)

# Calculate error
rmse = mean_squared_error(y_test, y_pred, squared=False)
print(f"Model RMSE: {rmse:.2f}")

# Check a few predictions
for i in range(5):
    print(f"Actual: {y_test.iloc[i]:.1f}, Predicted: {y_pred[i]:.1f}")
\`\`\`

## 🧠 What Happens During Training?

### Random Forest Learning Process:
1. **Bootstrap Sampling**: Creates multiple random subsets of training data
2. **Tree Building**: Builds decision trees on each subset
3. **Feature Selection**: Each tree uses random features for decisions
4. **Pattern Learning**: Trees learn different aspects of the data
5. **Ensemble Creation**: Combines all trees into final model

### The Math (Simplified):
- **Input**: (budget=50M, runtime=120min) 
- **Tree 1 Says**: "High budget + long runtime = 7.2 rating"
- **Tree 2 Says**: "Decent budget + good runtime = 7.5 rating"  
- **Tree 3 Says**: "Above average overall = 7.1 rating"
- **Final Prediction**: Average = 7.3 rating

## 📊 Training Metrics to Watch

### 1. Loss/Error Reduction
- Measures how "wrong" predictions are
- Should decrease as training progresses
- Different for classification vs regression

### 2. Training vs Validation Performance
- **Training error**: Performance on data model has seen
- **Validation error**: Performance on unseen data
- Gap indicates overfitting risk

### 3. Feature Importance
- Which features matter most for predictions?
- Helps understand what model learned
- Guides future feature engineering

## ⚠️ Common Training Pitfalls

### 1. Overfitting
**Problem:** Model memorizes training data instead of learning general patterns
**Symptoms:** 
- Perfect training accuracy, poor test performance
- Complex model with too many parameters
**Solutions:**
- Use validation data during training
- Regularization techniques
- Simpler models
- More training data

### 2. Underfitting  
**Problem:** Model too simple to capture real patterns
**Symptoms:**
- Poor performance on both training and test data
- High bias, low complexity
**Solutions:**
- More complex models
- Better features
- Remove regularization

### 3. Data Leakage
**Problem:** Future information accidentally included in training
**Example:** Using tomorrow's stock price to predict today's
**Prevention:** Careful data splitting and feature engineering

## 🎯 Interactive Training Exercise

Let's train a movie rating predictor step by step!

**Your Mission:** Complete the training code and achieve RMSE < 1.0

\`\`\`python
# Movie Rating Predictor Training
import pandas as pd
from sklearn.ensemble import RandomForestRegressor
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error

# Load data
movies = pd.read_csv('movie_data.csv')

# TODO: Select features for X
# Available: budget, runtime, director_experience, genre_encoded
X = movies[['budget', 'runtime']]  # Add more features!

y = movies['rating']

# Split data
X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42
)

# TODO: Create and configure your model
model = RandomForestRegressor(random_state=42)

# TODO: Train the model
# Your code here...

# Evaluate
y_pred = model.predict(X_test)
rmse = mean_squared_error(y_test, y_pred, squared=False)

print(f"RMSE: {rmse:.2f}")
if rmse < 1.0:
    print("🎉 Great job! Model trained successfully!")
else:
    print("💡 Try adding more features or tuning parameters")
\`\`\`

## 🚀 Advanced Training Tips

### 1. Hyperparameter Tuning
\`\`\`python
from sklearn.model_selection import GridSearchCV

# Define parameter grid
param_grid = {
    'n_estimators': [50, 100, 200],
    'max_depth': [5, 10, None],
    'min_samples_split': [2, 5, 10]
}

# Grid search with cross-validation
grid_search = GridSearchCV(
    RandomForestRegressor(random_state=42),
    param_grid,
    cv=5,
    scoring='neg_mean_squared_error'
)

grid_search.fit(X_train, y_train)
best_model = grid_search.best_estimator_
\`\`\`

### 2. Feature Importance Analysis
\`\`\`python
# Get feature importance scores
importances = model.feature_importances_
feature_names = X.columns

# Create importance ranking
importance_df = pd.DataFrame({
    'feature': feature_names,
    'importance': importances
}).sort_values('importance', ascending=False)

print("Feature Importance Ranking:")
print(importance_df)
\`\`\`

### 3. Learning Curves
\`\`\`python
from sklearn.model_selection import learning_curve

# Generate learning curve
train_sizes, train_scores, val_scores = learning_curve(
    model, X_train, y_train, cv=5
)

# Plot to visualize overfitting/underfitting
import matplotlib.pyplot as plt
plt.plot(train_sizes, train_scores.mean(axis=1), label='Training Score')
plt.plot(train_sizes, val_scores.mean(axis=1), label='Validation Score')
plt.legend()
plt.title('Learning Curve')
plt.show()
\`\`\`

## 🏁 Key Takeaways

✅ **Training is pattern recognition at scale**  
✅ **Always split data before training**  
✅ **Monitor both training and validation performance**  
✅ **Feature engineering often beats algorithm choice**  
✅ **Simple models with good data beat complex models with poor data**  
✅ **Overfitting is the #1 enemy - always validate!**

**Next:** We'll learn to evaluate how good your trained model really is! 📊`,
    lesson_type: 'coding',
    order_index: 5,
    estimated_duration_minutes: 35,
    video_url: null,
    resources: {
      starterCode: `# Movie Rating Predictor Training
import pandas as pd
from sklearn.ensemble import RandomForestRegressor
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error

# Load movie dataset
movies = pd.read_csv('movie_data.csv')

# TODO: Select features for prediction
X = movies[['budget', 'runtime']]  # Add more features to improve accuracy!
y = movies['rating']

# Split the data
X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)

# TODO: Create your model
model = RandomForestRegressor(random_state=42)

# TODO: Train the model
# Use model.fit(X_train, y_train)

# Make predictions and evaluate
y_pred = model.predict(X_test)
rmse = mean_squared_error(y_test, y_pred, squared=False)

print(f"Model RMSE: {rmse:.2f}")
if rmse < 1.0:
    print("🎉 Excellent! Your model is well-trained!")
`,
      expectedOutput: "Model training complete\\nModel RMSE: 0.85\\n🎉 Excellent! Your model is well-trained!"
    },
    is_preview: false,
    is_required: true,
    points_value: 25,
    created_at: '2024-01-20T16:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '2-6',
    course_id: '2',
    title: 'Evaluating Results',
    description: 'Learn to measure how well your model performs and understand different evaluation metrics.',
    content: `# Evaluating Your Model's Performance

Building a model is only half the battle. The real question is: **How good is it really?** Let's learn to evaluate model performance like a pro! 🔍

## 🎯 Why Evaluation Matters

**A model that can't be evaluated is a model that can't be trusted.**

### The Overconfident Student Problem
Imagine a student who claims to know calculus but has never taken a test. Would you trust them to engineer a bridge? Similarly, a model without proper evaluation is just educated guesswork.

**Real-world consequences:**
- 🏥 Medical diagnosis errors can be life-threatening
- 💰 Financial fraud detection failures cost millions
- 🚗 Self-driving car mistakes cause accidents
- 📧 Poor spam filters frustrate users

## 📊 Types of Model Evaluation

### Classification Metrics (Predicting Categories)

#### 1. Confusion Matrix: The Truth Table 📋
**What it shows:** How often your model confuses one class for another

\`\`\`
Movie Rating Prediction (High/Low)

                 PREDICTED
              High    Low
ACTUAL High    85     15     ← 85 correctly predicted high, 15 missed
       Low     12     88     ← 12 false alarms, 88 correctly predicted low
\`\`\`

**Key Terms:**
- **True Positives (TP)**: Correctly predicted positive (85)
- **True Negatives (TN)**: Correctly predicted negative (88)
- **False Positives (FP)**: Incorrectly predicted positive (12)
- **False Negatives (FN)**: Incorrectly predicted negative (15)

#### 2. Accuracy: The Overall Score 🎯
**Formula:** (TP + TN) / (TP + TN + FP + FN)
**Example:** (85 + 88) / (85 + 88 + 12 + 15) = 86.5%

**When to use:** Balanced datasets where all errors are equally bad
**When NOT to use:** Imbalanced data (99% not spam, 1% spam)

#### 3. Precision: Quality Over Quantity 🎪
**Formula:** TP / (TP + FP)  
**Question it answers:** "Of all positive predictions, how many were actually correct?"
**Example:** 85 / (85 + 12) = 87.6%

**Use case:** Email spam detection - you don't want good emails marked as spam!

#### 4. Recall (Sensitivity): Catching Everything 🕳️
**Formula:** TP / (TP + FN)
**Question it answers:** "Of all actual positives, how many did we catch?"
**Example:** 85 / (85 + 15) = 85%

**Use case:** Cancer diagnosis - you don't want to miss any cases!

### Regression Metrics (Predicting Numbers)

#### 1. Mean Squared Error (MSE) 📏
**What it measures:** Average squared difference between predictions and actual values
**Lower is better** (0 = perfect predictions)

#### 2. Root Mean Squared Error (RMSE) 📐
**What it measures:** Square root of MSE, in same units as target variable
**Example:** RMSE of 0.5 for movie ratings means predictions are typically within 0.5 stars

#### 3. Mean Absolute Error (MAE) 📊
**What it measures:** Average absolute difference between predictions and actual
**More robust to outliers than RMSE**

## 🧪 Interactive Exercise: Confusion Matrix Detective

**Scenario:** Your movie rating classifier made these predictions on 100 test movies:

\`\`\`
Confusion Matrix:
                 PREDICTED
              High    Low
ACTUAL High    42      8
       Low      5     45
\`\`\`

**Your Mission:** Calculate the key metrics and interpret the results!

**Questions:**
1. What's the accuracy?
2. What's the precision for "High" ratings?
3. What's the recall for "High" ratings?
4. Is this model better at identifying high-rated or low-rated movies?
5. What type of errors is the model making most?

### Solutions:
1. **Accuracy:** (42 + 45) / 100 = 87%
2. **Precision (High):** 42 / (42 + 5) = 89.4%
3. **Recall (High):** 42 / (42 + 8) = 84%
4. **Better at:** High-rated movies (higher precision and recall)
5. **Main error:** Missing high-rated movies (8 false negatives vs 5 false positives)

## 🚨 Common Evaluation Mistakes

### 1. Training on Test Data ❌
**Problem:** Using the same data for training and testing
**Why it's bad:** Like studying with the exam questions in advance
**Solution:** Always split data before training

### 2. Ignoring Class Imbalance ❌
**Problem:** 99% accuracy sounds great, but what if 99% of data is one class?
**Example:** Cancer detection with 1% cancer rate
**Solution:** Use precision, recall, and F1-score instead of accuracy

### 3. Cherry-Picking Metrics ❌
**Problem:** Only reporting the metric that looks best
**Solution:** Report multiple relevant metrics and explain trade-offs

### 4. Not Validating on Real-World Data ❌
**Problem:** Test data doesn't match production environment
**Solution:** Test on recent, representative data from actual use cases

## 🎪 Metric Selection Guide

### Email Spam Detection
- **Primary:** Precision (don't mark good emails as spam)
- **Secondary:** Recall (catch actual spam)
- **Balance:** F1-score

### Medical Diagnosis
- **Primary:** Recall (don't miss any diseases)
- **Secondary:** Precision (but false alarms are acceptable)
- **Additional:** Specificity (correctly identify healthy patients)

### Stock Price Prediction
- **Primary:** MAE or RMSE (minimize prediction error)
- **Business metric:** Profitability of trading strategy

### Fraud Detection
- **Primary:** Recall (catch all fraud)
- **Secondary:** Precision (minimize false alarms)
- **Balance:** Cost of missing fraud vs investigating false positives

## 💡 Advanced Evaluation Techniques

### 1. Cross-Validation 🔄
**Problem:** Single train/test split might be lucky or unlucky
**Solution:** Test on multiple different splits and average results

### 2. Learning Curves 📈
**Shows:** How performance changes with more training data
**Helps identify:** Overfitting vs underfitting

### 3. Feature Importance 🎯
**Shows:** Which features matter most for predictions
**Helps with:** Model interpretability and feature selection

### 4. Error Analysis 🔍
**Process:** Examine specific examples where model failed
**Benefits:** Understand model weaknesses and improvement opportunities

## 🏆 Model Evaluation Checklist

**Before Deployment:**
- [ ] Tested on data model has never seen
- [ ] Multiple relevant metrics calculated
- [ ] Performance compared to baseline/existing solution
- [ ] Edge cases and error modes understood
- [ ] Business impact of different error types considered
- [ ] Model performance documented and communicated

**During Production:**
- [ ] Monitor performance over time
- [ ] Check for distribution shifts
- [ ] Track business outcomes, not just technical metrics
- [ ] Set up alerts for performance degradation

## 🎬 Real-World Example: Netflix Movie Ratings

**The Challenge:** Predict whether users will rate movies 4+ stars

**Evaluation Strategy:**
- **Primary Metric:** Precision@K (accuracy of top-K recommendations)
- **Secondary Metric:** Recall (fraction of good movies recommended)
- **Business Metric:** User engagement and subscription retention
- **A/B Testing:** Compare new model against current production model

**Key Insight:** Technical accuracy matters, but business metrics determine success!

## 🚀 Key Takeaways

✅ **Different problems need different metrics**  
✅ **No single metric tells the whole story**  
✅ **Always test on unseen data**  
✅ **Understand the cost of different types of errors**  
✅ **Monitor performance in production**  
✅ **Business impact matters more than technical perfection**

**Remember:** A good evaluation strategy is your safety net. It prevents bad models from reaching production and helps you build confidence in good ones! 🛡️

*In our next lesson, we'll put your trained model to work making real predictions on new data...*`,
    lesson_type: 'interactive',
    order_index: 6,
    estimated_duration_minutes: 30,
    video_url: null,
    resources: {
      interactiveType: 'confusion-matrix-exercise',
      scenarios: [
        {
          title: 'Movie Rating Classifier Results',
          confusionMatrix: [[42, 8], [5, 45]],
          classes: ['High Rating', 'Low Rating'],
          tasks: [
            'Calculate accuracy, precision, and recall',
            'Identify which class is predicted better',
            'Determine the main type of error the model makes',
            'Suggest improvements based on the confusion matrix'
          ]
        },
        {
          title: 'Email Spam Detection Results', 
          confusionMatrix: [[950, 50], [10, 990]],
          classes: ['Spam', 'Not Spam'],
          tasks: [
            'Calculate precision and recall for spam detection',
            'Evaluate if this performance is good for email filtering',
            'Identify the business impact of false positives vs false negatives'
          ]
        }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 22,
    created_at: '2024-01-20T17:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '2-7',
    course_id: '2',
    title: 'Making Predictions',
    description: 'Use your trained model to make predictions on new data and understand confidence scores.',
    content: `# Making Predictions with Your Model

The moment of truth! Your model is trained, evaluated, and ready to make predictions. Let's put it to work on real data! 🚀

## 🎯 From Training to Production

**The Journey So Far:**
1. ✅ Collected and cleaned data
2. ✅ Chose and trained a model  
3. ✅ Evaluated performance
4. ✅ **NOW:** Make predictions on new, unseen data

**This is where machine learning becomes useful!** Your model can now make intelligent guesses about new movies, customers, or any other data it was trained on.

## 🔮 The Prediction Process

### Step 1: Prepare New Data
\`\`\`python
import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestRegressor

# New movies to predict ratings for
new_movies = pd.DataFrame({
    'budget': [25, 80, 150, 5, 200],  # Millions
    'runtime': [95, 135, 180, 88, 120],  # Minutes
    'director_experience': [3, 15, 25, 1, 12],  # Years
    'genre_encoded': [1, 2, 0, 1, 2],  # Comedy, Drama, Action
    'title': ['Small Indie Film', 'Big Budget Drama', 'Epic Fantasy', 
              'First-Time Director', 'Franchise Sequel']
})

print("Movies to predict:")
print(new_movies)
\`\`\`

### Step 2: Load Your Trained Model
\`\`\`python
# Assume we have our trained model from previous lesson
# In practice, you'd save/load the model:
# import joblib
# model = joblib.load('trained_movie_model.pkl')

# For this example, we'll use our existing trained model
print("Model loaded and ready for predictions!")
\`\`\`

### Step 3: Make Basic Predictions
\`\`\`python
# Extract features (same format as training data)
X_new = new_movies[['budget', 'runtime', 'director_experience', 'genre_encoded']]

# Make predictions
predictions = model.predict(X_new)

# Add predictions to dataframe
new_movies['predicted_rating'] = predictions

print("\\nPredictions:")
for i, row in new_movies.iterrows():
    print(f"{row['title']}: {row['predicted_rating']:.1f} stars")
\`\`\`

**Sample Output:**
\`\`\`
Small Indie Film: 6.8 stars
Big Budget Drama: 7.5 stars  
Epic Fantasy: 6.2 stars
First-Time Director: 5.9 stars
Franchise Sequel: 7.1 stars
\`\`\`

## 🎲 Understanding Confidence & Uncertainty

### Why Confidence Matters
Not all predictions are equally reliable. A good model should tell you:
- **What** it predicts
- **How confident** it is in that prediction

### Random Forest Confidence with Individual Tree Predictions
\`\`\`python
# Get predictions from individual trees
tree_predictions = []
for tree in model.estimators_[:10]:  # First 10 trees
    tree_pred = tree.predict(X_new)
    tree_predictions.append(tree_pred)

# Convert to array for easier analysis
tree_predictions = np.array(tree_predictions)

# Calculate confidence metrics
for i, movie in enumerate(new_movies['title']):
    movie_predictions = tree_predictions[:, i]
    
    mean_pred = np.mean(movie_predictions)
    std_pred = np.std(movie_predictions)
    min_pred = np.min(movie_predictions)
    max_pred = np.max(movie_predictions)
    
    print(f"\\n{movie}:")
    print(f"  Average Prediction: {mean_pred:.1f}")
    print(f"  Confidence Range: {min_pred:.1f} - {max_pred:.1f}")
    print(f"  Uncertainty (std): {std_pred:.2f}")
    
    # Interpret confidence
    if std_pred < 0.5:
        confidence = "High confidence 🎯"
    elif std_pred < 1.0:
        confidence = "Medium confidence 📊"
    else:
        confidence = "Low confidence ⚠️"
    
    print(f"  {confidence}")
\`\`\`

### Interpreting Confidence Scores

**High Confidence (low standard deviation):**
- Model is very sure about its prediction
- Input features match training data patterns well
- Multiple trees agree on the prediction

**Low Confidence (high standard deviation):**
- Model is uncertain about its prediction
- Input might be outside training data distribution
- Consider collecting more similar training examples

## 🛠️ Practical Prediction Scenarios

### Scenario 1: Real-Time Movie Rating Prediction
\`\`\`python
def predict_movie_rating(budget, runtime, director_exp, genre):
    """
    Predict movie rating with confidence assessment
    """
    # Prepare input data
    movie_data = np.array([[budget, runtime, director_exp, genre]])
    
    # Make prediction
    prediction = model.predict(movie_data)[0]
    
    # Get confidence from individual trees
    tree_preds = [tree.predict(movie_data)[0] for tree in model.estimators_[:20]]
    confidence = 1 / (1 + np.std(tree_preds))  # Higher std = lower confidence
    
    return {
        'predicted_rating': prediction,
        'confidence': confidence,
        'rating_range': (min(tree_preds), max(tree_preds))
    }

# Example usage
result = predict_movie_rating(
    budget=75,           # $75M budget
    runtime=110,         # 110 minutes
    director_exp=8,      # 8 years experience
    genre=1              # Drama
)

print(f"Predicted Rating: {result['predicted_rating']:.1f}")
print(f"Confidence: {result['confidence']:.1%}")
print(f"Range: {result['rating_range'][0]:.1f} - {result['rating_range'][1]:.1f}")
\`\`\`

### Scenario 2: Batch Predictions with Risk Assessment
\`\`\`python
def batch_predict_with_risk(movie_dataframe):
    """
    Predict ratings for multiple movies with risk assessment
    """
    X = movie_dataframe[['budget', 'runtime', 'director_experience', 'genre_encoded']]
    
    # Base predictions
    predictions = model.predict(X)
    
    # Calculate prediction intervals
    tree_predictions = np.array([tree.predict(X) for tree in model.estimators_])
    
    # For each movie, calculate statistics
    results = []
    for i in range(len(movie_dataframe)):
        movie_tree_preds = tree_predictions[:, i]
        
        result = {
            'title': movie_dataframe.iloc[i]['title'],
            'prediction': predictions[i],
            'confidence_95_lower': np.percentile(movie_tree_preds, 2.5),
            'confidence_95_upper': np.percentile(movie_tree_preds, 97.5),
            'risk_level': 'High' if np.std(movie_tree_preds) > 1.0 else 'Low'
        }
        results.append(result)
    
    return pd.DataFrame(results)

# Apply to our new movies
risk_assessment = batch_predict_with_risk(new_movies)
print("\\nRisk Assessment:")
print(risk_assessment)
\`\`\`

## ⚠️ Common Prediction Pitfalls

### 1. Distribution Shift 📊
**Problem:** New data differs from training data
**Example:** Model trained on 2020 movies predicting 2024 movies
**Warning Signs:**
- Predictions seem systematically off
- Very low confidence scores
- Features outside training ranges

**Solutions:**
- Regularly retrain with new data
- Monitor input data distributions
- Set up alerts for unusual inputs

### 2. Feature Leakage 🔍
**Problem:** Using future information to predict the past
**Example:** Using box office revenue to predict ratings (revenue comes after release)
**Prevention:** Carefully audit feature selection

### 3. Overconfident Predictions 🎯
**Problem:** Treating all predictions as equally reliable
**Solution:** Always consider confidence intervals and uncertainty

### 4. Ignoring Business Context 💼
**Problem:** Making technically correct but business-inappropriate predictions
**Example:** Recommending R-rated movies to children
**Solution:** Add business rule validation to predictions

## 🎬 Interactive Coding Exercise: Movie Recommendation System

**Your Mission:** Build a complete prediction system that recommends movies based on user preferences!

\`\`\`python
# Movie Recommendation System
import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestRegressor

class MovieRecommendationSystem:
    def __init__(self, trained_model):
        self.model = trained_model
        
    def predict_rating(self, budget, runtime, director_exp, genre):
        """Predict single movie rating"""
        # TODO: Implement prediction logic
        # Hint: Use self.model.predict()
        pass
        
    def get_confidence(self, budget, runtime, director_exp, genre):
        """Calculate prediction confidence"""
        # TODO: Get predictions from multiple trees
        # Calculate standard deviation as confidence measure
        pass
        
    def recommend_top_movies(self, candidate_movies, top_n=5):
        """Recommend top N movies from candidates"""
        # TODO: Predict ratings for all candidates
        # Return top N with highest predicted ratings
        pass
        
    def safe_recommendations(self, candidate_movies, min_confidence=0.7):
        """Only recommend movies with high confidence"""
        # TODO: Filter recommendations by confidence threshold
        pass

# Test your system
recommender = MovieRecommendationSystem(model)

# Example candidate movies
candidates = pd.DataFrame({
    'title': ['Action Blockbuster', 'Indie Drama', 'Comedy Hit', 'Sci-Fi Epic'],
    'budget': [200, 15, 40, 150],
    'runtime': [140, 95, 108, 160], 
    'director_experience': [20, 5, 12, 15],
    'genre_encoded': [0, 1, 2, 0]  # Action, Drama, Comedy, Action
})

# Your recommendations
top_movies = recommender.recommend_top_movies(candidates, top_n=2)
print("Top Recommendations:", top_movies)
\`\`\`

## 🏭 Production Deployment Considerations

### 1. Model Serving Infrastructure
- **API endpoints** for real-time predictions
- **Batch processing** for bulk predictions
- **Caching** for frequently requested predictions
- **Load balancing** for high-traffic scenarios

### 2. Monitoring & Alerting
\`\`\`python
# Example monitoring setup
def monitor_predictions(predictions, inputs):
    """Monitor prediction quality and input distributions"""
    
    # Check for unusual inputs
    budget_outliers = np.sum(inputs['budget'] > 500) / len(inputs)
    if budget_outliers > 0.1:  # More than 10% extreme budgets
        print("⚠️ Alert: Unusual budget distribution detected")
    
    # Check prediction distribution
    avg_prediction = np.mean(predictions)
    if avg_prediction < 3.0 or avg_prediction > 9.0:
        print("⚠️ Alert: Unusual prediction distribution")
    
    # Log statistics for analysis
    stats = {
        'timestamp': datetime.now(),
        'num_predictions': len(predictions),
        'avg_prediction': avg_prediction,
        'input_quality_score': calculate_input_quality(inputs)
    }
    
    return stats
\`\`\`

### 3. Model Versioning & Updates
- **Version control** for model artifacts
- **A/B testing** new models against current production
- **Rollback capabilities** if new model performs poorly
- **Gradual rollouts** to minimize risk

## 🎯 Key Best Practices

### Input Validation
✅ **Check data types and ranges**  
✅ **Handle missing values gracefully**  
✅ **Validate against business rules**  
✅ **Log unusual inputs for investigation**

### Output Interpretation  
✅ **Always include confidence measures**  
✅ **Provide prediction ranges, not just point estimates**  
✅ **Explain predictions when possible**  
✅ **Consider business context in recommendations**

### Continuous Learning
✅ **Collect feedback on predictions**  
✅ **Retrain models regularly with new data**  
✅ **Monitor for distribution shifts**  
✅ **A/B test model improvements**

## 🏆 Congratulations!

You've successfully learned to:
- ✅ Make predictions with trained models
- ✅ Assess prediction confidence and uncertainty  
- ✅ Handle real-world prediction scenarios
- ✅ Avoid common pitfalls
- ✅ Consider production deployment needs

**Your model is now ready to make intelligent decisions about new data!** 🎉

*In our final lesson, we'll explore what's next in your machine learning journey...*`,
    lesson_type: 'coding',
    order_index: 7,
    estimated_duration_minutes: 35,
    video_url: null,
    resources: {
      starterCode: `# Movie Recommendation System Implementation
import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestRegressor

class MovieRecommendationSystem:
    def __init__(self, trained_model):
        self.model = trained_model
        
    def predict_rating(self, budget, runtime, director_exp, genre):
        """Predict single movie rating with confidence"""
        # TODO: Prepare input data as array
        movie_data = np.array([[budget, runtime, director_exp, genre]])
        
        # TODO: Make prediction using self.model.predict()
        
        # TODO: Calculate confidence from tree predictions
        
        return prediction, confidence
        
    def recommend_top_movies(self, candidate_movies, top_n=5):
        """Recommend top N movies from candidates based on predicted ratings"""
        # TODO: Extract features from candidate_movies DataFrame
        
        # TODO: Predict ratings for all candidates
        
        # TODO: Add predictions to dataframe and sort by rating
        
        # TODO: Return top N movies
        
        pass

# Test data
candidates = pd.DataFrame({
    'title': ['Action Hero', 'Love Story', 'Space Opera', 'Indie Film'],
    'budget': [150, 30, 200, 8],
    'runtime': [120, 105, 140, 95],
    'director_experience': [15, 8, 20, 3],
    'genre_encoded': [0, 1, 0, 1]
})

# Create recommender and test
recommender = MovieRecommendationSystem(model)
recommendations = recommender.recommend_top_movies(candidates, top_n=2)
print("Top Recommendations:")
print(recommendations)
`,
      expectedOutput: "Top Recommendations:\\n    title  predicted_rating\\n1  Action Hero        7.8\\n2  Space Opera       7.5"
    },
    is_preview: false,
    is_required: true,
    points_value: 28,
    created_at: '2024-01-20T18:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  },
  {
    id: '2-8',
    course_id: '2',
    title: 'What\'s Next?',
    description: 'Celebrate your accomplishment and discover your next steps in machine learning.',
    content: `# What's Next? Your ML Journey Continues! 🚀

Congratulations! 🎉 You've just built your first machine learning model from start to finish. Take a moment to appreciate what you've accomplished—you're now officially a machine learning practitioner!

## 🏆 What You've Achieved

### Technical Skills Mastered ✅
- **Environment Setup**: Configured Python and essential ML libraries
- **Data Understanding**: Explored datasets and identified patterns
- **Data Cleaning**: Handled missing values, duplicates, and messy data
- **Model Selection**: Chose appropriate algorithms for your problem
- **Model Training**: Taught algorithms to recognize patterns in data
- **Performance Evaluation**: Measured and interpreted model accuracy
- **Prediction Making**: Used trained models to predict new outcomes
- **Confidence Assessment**: Understood prediction uncertainty

### Conceptual Understanding ✅
- **ML Workflow**: You know the complete end-to-end process
- **Problem Types**: You can distinguish classification from regression
- **Data Quality**: You recognize good data vs problematic data
- **Model Evaluation**: You understand different metrics and their trade-offs
- **Real-world Deployment**: You consider practical constraints and monitoring

### Mindset Development ✅
- **Data-Driven Thinking**: You approach problems systematically with data
- **Iterative Improvement**: You understand ML is about continuous refinement
- **Critical Evaluation**: You question results and seek validation
- **Practical Focus**: You consider business impact, not just technical accuracy

## 🌟 You're Part of the ML Community Now!

**Before this course:** Machine learning seemed like magic
**Now:** You understand it's systematic pattern recognition with data

**You can now:**
- Build predictive models for real problems
- Communicate ML concepts to others
- Evaluate when ML is (and isn't) the right solution
- Continue learning advanced techniques confidently

## 🎯 Immediate Next Steps (Choose Your Adventure!)

### Path 1: Deepen Your Foundation 📚
**Take "Data Fundamentals for ML"**
- Master data collection, cleaning, and visualization
- Learn advanced feature engineering techniques
- Understand statistical concepts underlying ML
- Build expertise in data pipeline design

**Best for:** Analytical minds who want to become data experts

### Path 2: Expand Your Toolkit 🛠️
**Explore More Algorithms:**
- **Deep Learning**: Neural networks for complex patterns
- **Ensemble Methods**: Combining multiple models for better performance
- **Unsupervised Learning**: Finding hidden patterns without labels
- **Time Series**: Predicting future values from historical data

**Best for:** Those who want to tackle more complex problems

### Path 3: Specialize by Domain 🎯
Choose an application area that excites you:

**Computer Vision 📷**
- Image classification and object detection
- Medical imaging analysis
- Autonomous vehicle perception
- Content moderation and analysis

**Natural Language Processing 💬**
- Chatbots and virtual assistants
- Sentiment analysis and social media monitoring
- Language translation and text summarization
- Document analysis and information extraction

**Recommendation Systems 🎬**
- E-commerce product recommendations
- Content discovery for media platforms
- Personalized marketing and advertising
- Social network friend suggestions

**Finance & Business 💰**
- Fraud detection and risk assessment
- Algorithmic trading and portfolio optimization
- Customer churn prediction
- Price optimization and demand forecasting

### Path 4: Build Real Projects 🏗️
**Project Ideas to Showcase Your Skills:**

**Beginner Projects:**
1. **Personal Movie Recommender**: Expand your course project
2. **Stock Price Tracker**: Predict daily price movements
3. **Weather Predictor**: Forecast temperature based on historical data
4. **Customer Segmentation**: Group customers by purchasing behavior

**Intermediate Projects:**
1. **Social Media Sentiment Dashboard**: Analyze Twitter sentiment in real-time
2. **Health Monitoring App**: Predict health risks from activity data
3. **Smart Home System**: Learn and predict household patterns
4. **Sports Analytics Platform**: Predict game outcomes and player performance

## 🧠 Continued Learning Resources

### Free Online Courses 🆓
- **Andrew Ng's Machine Learning Course** (Coursera): The classic intro course
- **Fast.ai Practical Deep Learning**: Hands-on approach to neural networks
- **MIT Introduction to Machine Learning**: Academic perspective with strong theory
- **Google AI Education**: Practical guides and tutorials

### Books for Deeper Understanding 📖
- **"Hands-On Machine Learning"** by Aurélien Géron: Practical Python focus
- **"Pattern Recognition and Machine Learning"** by Christopher Bishop: Mathematical foundations
- **"The Elements of Statistical Learning"**: Classic reference (free PDF)
- **"Weapons of Math Destruction"** by Cathy O'Neil: Ethics and societal impact

### Practice Platforms 💻
- **Kaggle**: Competitions, datasets, and community
- **Google Colab**: Free GPU access for experimentation
- **GitHub**: Share projects and collaborate with others
- **Papers With Code**: Latest research with implementation code

### Community & Networking 🤝
- **Local ML Meetups**: Find groups in your city
- **Reddit**: r/MachineLearning, r/datascience, r/LearnMachineLearning
- **Twitter**: Follow ML researchers and practitioners
- **LinkedIn**: Connect with ML professionals
- **Discord/Slack**: Join ML communities for real-time discussion

## 🔥 Advanced Topics to Explore Later

### Technical Deep Dives
- **MLOps**: Deploying and managing ML systems in production
- **Model Interpretability**: Understanding why models make specific decisions
- **Transfer Learning**: Using pre-trained models as starting points
- **Reinforcement Learning**: Teaching agents to learn through trial and error
- **Generative Models**: Creating new data (images, text, music) with AI

### Domain Expertise
- **AI Ethics**: Building fair and responsible ML systems
- **Edge Computing**: Running ML on mobile devices and IoT
- **AutoML**: Automated machine learning pipeline creation
- **Quantum ML**: Exploring quantum computing for machine learning
- **Federated Learning**: Training models across distributed data sources

## 🚀 Career Opportunities in ML

### Job Roles You Could Pursue
**Entry Level (0-2 years):**
- **Junior Data Scientist**: Analyze data and build basic models
- **ML Engineer I**: Implement and deploy ML systems
- **Data Analyst**: Extract insights from data for business decisions
- **Research Assistant**: Support ML research projects

**Mid Level (2-5 years):**
- **Data Scientist**: Lead ML projects and mentor juniors
- **Senior ML Engineer**: Design and optimize ML infrastructure
- **Product Manager (ML)**: Guide ML product development
- **ML Consultant**: Help businesses implement ML solutions

**Senior Level (5+ years):**
- **Principal Data Scientist**: Set technical direction for ML teams
- **ML Research Scientist**: Develop new algorithms and techniques
- **Director of AI**: Lead organization-wide AI initiatives
- **Chief Data Officer**: Executive oversight of data and ML strategy

### Industry Sectors Hiring ML Talent
- **Technology**: Google, Facebook, Amazon, Microsoft, startups
- **Finance**: Banks, trading firms, fintech companies, insurance
- **Healthcare**: Hospitals, pharmaceutical companies, health tech
- **Retail**: E-commerce, logistics, supply chain optimization
- **Transportation**: Autonomous vehicles, ride-sharing, logistics
- **Entertainment**: Streaming services, gaming, content creation
- **Government**: Defense, intelligence, public services, research

## 💡 Wisdom from Your Journey

### Key Insights You've Learned
1. **Data Quality Trumps Algorithm Sophistication**: Great data with simple models beats poor data with complex models
2. **Iteration is Essential**: ML is rarely perfect on the first try—embrace the iterative process
3. **Business Context Matters**: Technical accuracy means nothing without real-world impact
4. **Evaluation is Critical**: You can't improve what you don't measure properly
5. **Simplicity Often Wins**: Complex solutions are harder to understand, debug, and maintain

### Mindset for Continued Growth
- **Stay Curious**: Always ask "why" and "what if"
- **Embrace Failure**: Every failed model teaches valuable lessons
- **Think Systems**: Consider the entire pipeline, not just the algorithm
- **Keep Learning**: The field evolves rapidly—continuous learning is essential
- **Share Knowledge**: Teaching others reinforces your own understanding

## 🎪 Fun ML Facts to Impress Your Friends

🧠 **Your brain processes visual information 60,000x faster than text**—inspiring computer vision research

📱 **Your smartphone has more computing power than all of NASA in 1969**—making personal ML possible

🎬 **Netflix saves $1 billion annually through ML recommendations**—keeping subscribers engaged

🚗 **Self-driving cars analyze 1 TB of data per hour**—equivalent to 200,000 photos

🐱 **The first neural network was inspired by how cats' visual cortex processes images**—proving nature is the best teacher

## 🌈 A Personal Message

**You did it!** 🎉

When you started this course, machine learning might have seemed like an exclusive club of PhD researchers and tech giants. Now you know the truth: ML is a tool, like Excel or PowerPoint—powerful when used skillfully, but learnable by anyone willing to put in the effort.

**You've proven you can:**
- Learn complex technical concepts
- Solve problems systematically with data
- Build tools that make intelligent decisions
- Think critically about technology's impact

**This is just the beginning.** The skills you've developed here will compound as you continue learning. Every new technique you master, every project you complete, and every problem you solve makes you more capable of tackling bigger challenges.

**The future needs thoughtful ML practitioners**—people who understand both the technical possibilities and the human implications of artificial intelligence. You're now equipped to be one of them.

## 🎯 Your Next Action

Before you close this lesson, do ONE of these:

1. **Enroll in your next course** (Data Fundamentals or another specialization)
2. **Start a personal project** using the skills you've learned
3. **Join an ML community** online or in your city
4. **Share your learning** with a friend or on social media
5. **Apply to an ML-related job or internship**

**Don't wait for perfect conditions.** The best time to start your next challenge was yesterday. The second-best time is now.

## 🚀 Final Words

**Machine learning is not magic—it's math, statistics, and careful engineering.** But when done well, it can feel magical. You now have the foundation to build systems that learn, adapt, and make intelligent decisions.

**Use these powers wisely.** Build things that help people. Solve problems that matter. Ask hard questions about fairness, bias, and impact. The world needs ML practitioners who care about more than just accuracy metrics.

**Keep building. Keep learning. Keep questioning.**

**Welcome to the future. You're ready to help shape it.** 🌟

---

*Congratulations on completing "Your First ML Model"! We can't wait to see what you build next.* 💫`,
    lesson_type: 'text',
    order_index: 8,
    estimated_duration_minutes: 15,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: false,
    points_value: 20,
    created_at: '2024-01-20T19:00:00Z',
    updated_at: '2024-03-10T16:20:00Z'
  }
];

// Course 3: Data Fundamentals for ML (7 lessons)
export const DATA_FUNDAMENTALS_LESSONS: Lesson[] = [
  {
    id: '3-1',
    course_id: '3',
    title: 'What is Data?',
    description: 'Understand the foundation of all machine learning: data.',
    content: `# What is Data?

In machine learning, data isn't just numbers in a spreadsheet—it's the raw material from which artificial intelligence emerges. Let's explore what makes data so powerful!

## 🧠 Data: The Brain Food of AI

**Data is information that can be processed by computers.** But not all data is created equal. Think of data as ingredients for cooking:
- **Quality ingredients** → **Delicious meal**  
- **Poor ingredients** → **Disappointing results**

Similarly:
- **Good data** → **Smart AI systems**
- **Bad data** → **Unreliable predictions**

## 📊 Types of Data in ML

### 1. Structured Data 📋
**What it is:** Organized in rows and columns (like Excel spreadsheets)

**Examples:**
- Customer databases (name, age, purchases)
- Financial records (date, amount, category)
- Sensor readings (time, temperature, humidity)
- Survey responses (rating scales, yes/no questions)

**Why ML loves it:** Easy to process, clear relationships, consistent format

### 2. Unstructured Data 🌊
**What it is:** No predefined format or organization

**Examples:**
- 📱 Social media posts and comments
- 📧 Email content
- 📷 Photos and videos  
- 🎵 Audio recordings
- 📄 Documents and articles

**ML challenge:** Must be converted to numbers before algorithms can use it

### 3. Semi-Structured Data 📋📊
**What it is:** Some organization but not fully structured

**Examples:**
- JSON files (web data)
- XML documents
- HTML web pages
- Log files

## 🏗️ The Anatomy of a Dataset

Let's break down a typical ML dataset:

\`\`\`
Customer Purchase Dataset

| CustomerID | Age | Income | City     | Product   | Price | Rating |
|------------|-----|--------|----------|-----------|-------|--------|
| 001        | 28  | 65000  | Seattle  | Laptop    | 1200  | 4.5    |
| 002        | 35  | 45000  | Austin   | Phone     | 800   | 4.0    |
| 003        | 42  | 85000  | Boston   | Tablet    | 600   | 4.8    |
\`\`\`

### Key Components:
- **Rows (Observations)**: Each customer purchase
- **Columns (Features/Variables)**: Characteristics we track
- **Target Variable**: What we want to predict (Rating)
- **Feature Variables**: What we use to predict (Age, Income, etc.)

## 🎯 Data for Different ML Tasks

### Classification Tasks 🏷️
**Goal:** Predict categories
**Data needs:**
- Clear category labels
- Balanced examples of each class
- Features that distinguish categories

**Example:** Email spam detection
- **Features:** Sender, subject line, content keywords
- **Labels:** "Spam" or "Not Spam"

### Regression Tasks 📈
**Goal:** Predict continuous numbers
**Data needs:**
- Numeric target values
- Features correlated with target
- Sufficient range in target values

**Example:** House price prediction
- **Features:** Size, location, bedrooms, age
- **Target:** Sale price ($)

### Clustering Tasks 🎯
**Goal:** Find hidden groups
**Data needs:**
- Relevant features for grouping
- No predefined labels needed
- Sufficient variability to form clusters

**Example:** Customer segmentation
- **Features:** Purchase history, demographics
- **Goal:** Discover customer types

## 📏 Data Quality: The Make-or-Break Factor

### The Good ✅
**Complete Data:**
- No missing values in critical columns
- Full coverage of the problem domain

**Accurate Data:**
- Minimal errors or outliers
- Validated and verified information

**Relevant Data:**
- Features that actually relate to your target
- Recent and representative examples

**Sufficient Data:**
- Enough examples to learn patterns
- Balanced representation of all scenarios

### The Bad ❌
**Incomplete Data:**
- Missing critical information
- Gaps that bias your model

**Inaccurate Data:**
- Typos, measurement errors
- Outdated information

**Irrelevant Data:**
- Features unrelated to your goal
- Noise that confuses the algorithm

**Insufficient Data:**
- Too few examples to learn from
- Unbalanced representation

## 🔍 Real-World Data Examples

### E-commerce Recommendation System
**Data Sources:**
- 🛒 Purchase history
- 👀 Browsing behavior  
- ⭐ Product ratings
- 👥 User demographics
- 📝 Product descriptions

**Challenge:** Combine multiple data types to predict what users might buy

### Medical Diagnosis AI
**Data Sources:**
- 🏥 Patient records (structured)
- 🔬 Lab results (numeric)
- 📋 Doctor notes (text)
- 🖼️ Medical images (visual)
- 🧬 Genetic data (complex)

**Challenge:** Life-critical accuracy requirements

### Social Media Sentiment Analysis
**Data Sources:**
- 💬 Post content (text)
- 😊 Emoji usage (symbolic)
- 👥 User interactions (network data)
- ⏰ Timing patterns (temporal)
- 🌍 Location data (geographic)

**Challenge:** Understanding context and sarcasm

## 🎪 The Data Lifecycle

### 1. Generation 🌱
Data is created through:
- User interactions (clicks, purchases)
- Sensors and IoT devices
- Surveys and forms
- Business transactions

### 2. Collection 📦
Gathering data from various sources:
- Databases and APIs
- Web scraping
- File uploads
- Real-time streams

### 3. Storage 🏠
Organizing data for access:
- Data warehouses
- Cloud storage
- Databases
- Data lakes

### 4. Processing 🔧
Preparing data for use:
- Cleaning and validation
- Transformation and formatting
- Feature engineering
- Quality assurance

### 5. Analysis 📊
Extracting insights:
- Exploratory data analysis
- Statistical testing
- Machine learning modeling
- Visualization and reporting

## 💡 Fun Facts About Data

🌐 **Scale:** Google processes 40,000 searches per second, creating massive datasets

📈 **Growth:** 90% of the world's data was created in the last 2 years

🎬 **Netflix:** Uses viewing data from 200+ million subscribers to decide which shows to create

🚗 **Tesla:** Self-driving cars generate 25 GB of data per hour

📱 **Smartphones:** Generate data through 14+ sensors (GPS, camera, microphone, etc.)

## 🎯 Key Takeaways

1. **Data is the foundation** of all machine learning—no data, no intelligence
2. **Quality matters more than quantity**—better to have 1000 good examples than 10,000 poor ones
3. **Different problems need different data**—classification, regression, and clustering each have specific requirements
4. **Real-world data is messy**—expect to spend significant time cleaning and preparing data
5. **Context is crucial**—understanding where data comes from helps you use it effectively

## 🚀 What's Next?

Now that you understand what data is, we'll explore:
- Where to find good datasets for your projects
- How to evaluate data quality
- Tools and techniques for data exploration
- Best practices for data collection and storage

**Remember:** In machine learning, garbage in = garbage out. Invest time in understanding and improving your data—it's the best investment you can make in your model's success!

*In our next lesson, we'll go hunting for high-quality datasets and learn to spot the difference between data gold and data fool's gold...*`,
    lesson_type: 'text',
    order_index: 1,
    estimated_duration_minutes: 18,
    video_url: null,
    resources: {},
    is_preview: true,
    is_required: true,
    points_value: 15,
    created_at: '2024-02-01T13:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-2',
    course_id: '3',
    title: 'Finding Good Data',
    description: 'Discover where to find high-quality datasets and learn to evaluate data sources.',
    content: `# Finding Good Data

Great data is like treasure - it's valuable, sometimes hidden, and requires skill to recognize. Let's become data treasure hunters! 🏴‍☠️

## 🌟 The Gold Standard: What Makes Data "Good"?

### Quality Indicators ✅
- **Recent**: Data from the last few years (unless studying historical trends)
- **Relevant**: Directly relates to your problem domain  
- **Complete**: Minimal missing values in critical fields
- **Accurate**: Comes from reliable, verified sources
- **Sufficient Size**: Enough examples to learn meaningful patterns
- **Well-Documented**: Clear descriptions of what each field means

### Red Flags 🚩
- **Too Perfect**: Real data has some messiness - perfection suggests synthetic/fake data
- **Massive Missing Values**: >30% missing in key columns
- **Outdated**: 5+ year old data for rapidly changing domains
- **Biased Sources**: Data from limited demographics or regions
- **Poor Documentation**: No explanation of fields or collection methods
- **Questionable Provenance**: Unknown or unreliable source

## 🏛️ Top-Tier Data Sources

### 1. Kaggle Datasets 🎯
**What it is:** World's largest data science community platform
**Why it's great:**
- ✅ 50,000+ datasets across every domain
- ✅ Community voting and feedback on quality
- ✅ Detailed descriptions and usage examples
- ✅ Regular competitions with high-quality datasets
- ✅ Free to access

**Best for:** Learning, competitions, proof-of-concepts
**Examples:**
- Titanic survival data (classic beginner dataset)
- House prices prediction
- Customer churn analysis
- Image classification datasets

**Pro Tips:**
- Sort by "Most Votes" for quality datasets
- Read the comments for insights and issues
- Check "Kernels" tab for analysis examples

### 2. Government Open Data 🏛️
**Why governments share data:**
- Transparency initiatives
- Public research support  
- Economic development

**Top Sources:**
- **Data.gov (US)**: 200,000+ datasets from federal agencies
- **Open Government Data (UK)**: Health, crime, education data
- **European Data Portal**: EU-wide open data
- **World Bank Open Data**: Global development indicators
- **WHO Global Health Observatory**: Health statistics worldwide

**Best for:** Social research, public policy analysis, economics
**Examples:**
- COVID-19 tracking data
- Census and demographic data
- Crime statistics by city
- Economic indicators and employment data
- Climate and weather data

### 3. Academic Research Datasets 🎓
**UCI Machine Learning Repository**
- 500+ datasets used in ML research
- Thoroughly documented and tested
- Classic datasets for algorithm comparison
- Free access for research and education

**Paper-Specific Datasets:**
- Many research papers release their datasets
- Often cutting-edge, domain-specific
- Usually high-quality due to peer review process

**Best for:** Research projects, algorithm benchmarking

### 4. Industry APIs and Platforms 🏢

**Social Media APIs:**
- Twitter API: Tweets, user behavior, trends
- Reddit API: Comments, posts, community data  
- Instagram API: Images, engagement metrics

**Financial Data:**
- Yahoo Finance: Stock prices, market data
- Alpha Vantage: Real-time and historical financial data
- Quandl: Economic and financial datasets

**Other APIs:**
- Google Trends: Search volume data
- Spotify API: Music and listening data
- OpenWeather: Weather and climate data

**Best for:** Real-time applications, current trends analysis

## 🔍 Dataset Evaluation Checklist

### Before You Download ✅

**1. Source Credibility**
- [ ] Who collected this data?
- [ ] What's their reputation/expertise?
- [ ] Why was it collected?
- [ ] When was it last updated?

**2. Documentation Quality**
- [ ] Are all columns clearly explained?
- [ ] Is the collection methodology described?
- [ ] Are there known limitations or biases?
- [ ] Are there usage examples provided?

**3. Size and Scope**
- [ ] Is the dataset large enough for your needs?
- [ ] Does it cover the time period you need?
- [ ] Are all relevant categories represented?
- [ ] Is the geographic coverage appropriate?

**4. Legal and Ethical**
- [ ] Is the data freely usable?
- [ ] Are there any licensing restrictions?
- [ ] Does it contain sensitive personal information?
- [ ] Was consent obtained for data collection?

### After You Download ✅

**Quick Quality Check:**
\`\`\`python
import pandas as pd

# Load and inspect
df = pd.read_csv('your_dataset.csv')

# Basic info
print(f"Shape: {df.shape}")
print(f"Memory usage: {df.memory_usage().sum() / 1024**2:.1f} MB")

# Missing values
missing_pct = (df.isnull().sum() / len(df)) * 100
print("Missing values (%):")
print(missing_pct[missing_pct > 0].sort_values(ascending=False))

# Data types
print("\\nData types:")
print(df.dtypes.value_counts())

# Duplicates
print(f"\\nDuplicate rows: {df.duplicated().sum()}")

# First look at data
print("\\nFirst 5 rows:")
print(df.head())
\`\`\`

## 🎯 Domain-Specific Gold Mines

### Healthcare 🏥
- **MIMIC Critical Care Database**: ICU patient data
- **Cancer Genome Atlas**: Genomic data for cancer research
- **NIH National Cancer Institute**: Various cancer datasets
- **WHO Global Health Observatory**: International health statistics

### Finance 💰
- **Federal Reserve Economic Data (FRED)**: US economic indicators
- **European Central Bank**: EU economic data
- **International Monetary Fund**: Global financial statistics
- **Cryptocurrency APIs**: Real-time crypto market data

### Retail & E-commerce 🛒
- **Amazon Product Data**: Product reviews and ratings
- **Instacart Market Basket Analysis**: Shopping behavior data
- **Online Retail Dataset**: UK retailer transaction data

### Transportation 🚗
- **NYC Taxi Trip Data**: Millions of taxi rides with GPS coordinates
- **Citibike Trip Histories**: Bike sharing usage patterns
- **Aviation Weather**: Flight delays and weather correlation

### Social Media & Text 📱
- **Common Crawl**: Web crawl data for text analysis
- **Stanford Sentiment Treebank**: Sentiment analysis dataset
- **20 Newsgroups**: Classic text classification dataset

## ⚠️ Common Data Hunting Mistakes

### 1. Dataset Too Small
**Problem:** Trying to solve complex problems with <1,000 examples
**Solution:** Look for larger datasets or consider simpler problems

### 2. Confusing Size with Quality  
**Problem:** "This dataset has 1 million rows, it must be good!"
**Solution:** Check for duplicates, missing values, and actual information content

### 3. Ignoring Data Bias
**Problem:** Using data that doesn't represent your target population
**Example:** Training on data from one country, deploying globally
**Solution:** Understand your data's limitations and scope

### 4. Legal Issues
**Problem:** Using copyrighted or private data without permission
**Solution:** Always check licensing and terms of use

### 5. Outdated Data
**Problem:** Using 2015 social media data to understand current trends
**Solution:** Check collection dates and relevance to current context

## 💡 Pro Data Hunter Tips

### 1. Start with Known Good Datasets
**For beginners, try these "classic" datasets:**
- Iris flower classification (simple, clean)
- Boston housing prices (regression)
- MNIST handwritten digits (image classification)
- Titanic survival (binary classification)

### 2. Join the Community
- Follow Kaggle discussions
- Join r/MachineLearning and r/datasets on Reddit
- Participate in data science Twitter conversations
- Attend local data science meetups

### 3. Create Your Own Data
**Sometimes the best data is data you collect:**
- Web scraping (respect robots.txt and terms of service)
- APIs for real-time data
- Surveys and experiments
- Sensor data collection

### 4. Data Partnerships
**For business applications:**
- Partner with companies in your domain
- Academic collaborations
- Government partnership programs
- Industry consortiums

## 🚀 Your Data Hunting Toolkit

### Essential Python Libraries:
\`\`\`python
# Data manipulation and analysis
import pandas as pd
import numpy as np

# Web scraping
import requests
import BeautifulSoup

# API interactions  
import json
import urllib

# Data visualization for quick checks
import matplotlib.pyplot as plt
import seaborn as sns
\`\`\`

### Useful Websites to Bookmark:
- **Dataset Search Engines:**
  - Google Dataset Search
  - Microsoft Research Open Data
  - AWS Open Data
  - DataHub.io

- **Academic Sources:**
  - Papers With Code (datasets from research papers)
  - IEEE Dataport
  - Harvard Dataverse

## 🏆 Your Action Plan

1. **Start Small**: Pick a simple, well-documented dataset for your first project
2. **Build Your Library**: Bookmark good data sources for future use
3. **Practice Evaluation**: Use the checklist on 3-5 different datasets
4. **Join Communities**: Follow data science forums and discussions
5. **Document Your Findings**: Keep notes on what makes datasets good/bad

**Remember:** Great models start with great data. Time spent finding quality datasets is time well invested! 🎯

*Next up: We'll learn to explore and understand your chosen dataset like a detective...* 🔍`,
    lesson_type: 'text',
    order_index: 2,
    estimated_duration_minutes: 22,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: true,
    points_value: 18,
    created_at: '2024-02-01T14:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-3',
    course_id: '3',
    title: 'Data Types and Formats',
    description: 'Learn to identify and work with different types of data in machine learning.',
    content: `# Data Types and Formats in Machine Learning

Not all data is created equal! Understanding different data types is crucial for choosing the right ML approaches and preprocessing techniques. Let's explore the data universe! 🌍

## 🎯 The Big Three: Structured, Semi-Structured, and Unstructured

### Structured Data 📊
**Definition:** Data organized in a fixed format with clear relationships

**Characteristics:**
- Rows and columns (like spreadsheets)
- Predefined schema
- Easy to search and analyze
- Fits neatly in databases

**Examples:**
- 💳 Credit card transactions (date, amount, merchant, category)
- 🏥 Patient records (age, weight, blood pressure, diagnosis)
- 📈 Stock prices (symbol, date, open, high, low, close, volume)
- 🛒 Sales data (product, quantity, price, customer_id, date)

**ML Advantage:** Ready to use with minimal preprocessing

### Semi-Structured Data 🔗
**Definition:** Some organization but not fully structured

**Characteristics:**
- Has tags, keys, or markers for organization
- Flexible schema
- Self-describing metadata
- Between structured and unstructured

**Examples:**
- 🌐 JSON files from web APIs
- 📧 Email headers and metadata
- 📱 XML documents
- 🏷️ Tagged social media posts

**ML Challenge:** Need to extract structured elements before use

### Unstructured Data 🌊
**Definition:** No predefined format or organization

**Characteristics:**
- Free-form content
- Human-readable but machine-challenging
- Rich information content
- Requires significant preprocessing

**Examples:**
- 📝 Text documents and articles
- 📷 Images and photos
- 🎵 Audio recordings
- 🎬 Videos
- 📱 Social media posts

**ML Challenge:** Must be converted to numbers before algorithms can process

## 🔢 Data Types by Variable Nature

### Numerical Data 📊

#### Continuous (Real Numbers)
**Definition:** Can take any value within a range
**Examples:**
- Temperature: 23.7°C, 98.6°F
- Height: 5.8 feet, 175.3 cm
- Stock prices: $142.67, €88.23
- Time duration: 2.5 hours, 127.3 seconds

**ML Usage:** Perfect for regression, statistical analysis

#### Discrete (Countable Numbers)  
**Definition:** Whole numbers, specific countable values
**Examples:**
- Number of children: 0, 1, 2, 3
- Website clicks: 47, 152, 0
- Items purchased: 1, 3, 12
- Years of experience: 2, 15, 0

**ML Usage:** Can be treated as numerical or categorical

### Categorical Data 🏷️

#### Nominal (No Order)
**Definition:** Categories with no inherent order or ranking
**Examples:**
- Colors: Red, Blue, Green, Yellow
- Countries: USA, Canada, Germany, Japan
- Product categories: Electronics, Clothing, Books
- Gender: Male, Female, Non-binary

**ML Preprocessing:** Usually encoded as numbers (one-hot encoding)

#### Ordinal (Ordered Categories)
**Definition:** Categories with clear order or ranking
**Examples:**
- Education level: High School < Bachelor's < Master's < PhD
- Movie ratings: Poor < Fair < Good < Excellent
- Size: Small < Medium < Large < Extra Large
- Income brackets: Low < Middle < Upper Middle < High

**ML Preprocessing:** Can be encoded with meaningful numbers (1, 2, 3, 4)

## 📁 Common Data Formats

### Tabular Formats 📋

#### CSV (Comma-Separated Values)
**Pros:** Universal, simple, lightweight
**Cons:** No data types, poor handling of complex data
**Best for:** Clean numerical and categorical data

#### Excel/Sheets
**Pros:** Human-readable, supports formatting
**Cons:** Proprietary, can corrupt data, size limits
**Best for:** Business data, initial exploration

#### Parquet
**Pros:** Compressed, fast reads, preserves data types
**Cons:** Binary format, not human-readable
**Best for:** Big data processing, production pipelines

### Database Formats 🗄️

#### SQL Databases
**Examples:** PostgreSQL, MySQL, SQL Server
**Pros:** ACID compliance, relationships, powerful queries
**Best for:** Transactional data, complex relationships

#### NoSQL Databases  
**Examples:** MongoDB, Cassandra, Redis
**Pros:** Flexible schema, horizontal scaling
**Best for:** Unstructured data, web applications

### Text Formats 📝

#### JSON (JavaScript Object Notation)
**Pros:** Human-readable, web-standard, hierarchical
**Cons:** Can be verbose, no data types
**Best for:** API responses, configuration files

#### XML (eXtensible Markup Language)
**Pros:** Self-documenting, validation support
**Cons:** Verbose, complex parsing
**Best for:** Documents, legacy systems

### Media Formats 🎨

#### Images
**Common formats:** JPEG, PNG, TIFF, SVG
**ML considerations:** Color depth, resolution, compression

#### Audio
**Common formats:** WAV, MP3, FLAC
**ML considerations:** Sample rate, bit depth, channels

#### Video
**Common formats:** MP4, AVI, MOV
**ML considerations:** Frame rate, resolution, codecs

## 🎮 Interactive Exercise: Data Type Detective

**Your Mission:** Classify these data examples! Drag each to the correct category.

### Dataset Examples:
1. Customer ages: 23, 45, 67, 34, 52
2. Product ratings: "Excellent", "Good", "Fair", "Poor"  
3. Customer reviews: "This product is amazing! I love the design..."
4. Purchase amounts: $23.45, $156.78, $45.00
5. Zip codes: 90210, 10001, 60601
6. Movie genres: "Comedy", "Drama", "Action", "Horror"
7. Satisfaction scores: 1, 2, 3, 4, 5
8. Email addresses: "user@email.com", "customer@company.org"

### Categories:
- **Numerical Continuous**
- **Numerical Discrete** 
- **Categorical Nominal**
- **Categorical Ordinal**
- **Unstructured Text**

### Solutions:
1. **Numerical Continuous** - Can be any real number
2. **Categorical Ordinal** - Clear ranking order
3. **Unstructured Text** - Free-form content
4. **Numerical Continuous** - Dollar amounts with decimals
5. **Categorical Nominal** - Numbers but represent categories, not quantities
6. **Categorical Nominal** - No inherent order between genres  
7. **Categorical Ordinal** - Clear scale from low to high satisfaction
8. **Categorical Nominal** - Text identifiers, no mathematical meaning

## ⚠️ Common Data Type Mistakes

### 1. Treating Categorical Numbers as Numerical ❌
**Problem:** ZIP codes, phone numbers, IDs treated as numbers
**Example:** Taking the average of ZIP codes (meaningless!)
**Solution:** Recognize these as categorical identifiers

### 2. Ignoring Ordinal Relationships ❌
**Problem:** Treating ordinal data as nominal
**Example:** Encoding education levels randomly instead of preserving order
**Solution:** Use ordinal encoding or maintain order in analysis

### 3. Wrong Encoding for Categorical Data ❌
**Problem:** Using simple numerical encoding for nominal categories
**Example:** Encoding colors as 1=Red, 2=Blue, 3=Green implies order
**Solution:** Use one-hot encoding for nominal categories

### 4. Losing Information in Conversion ❌
**Problem:** Converting continuous to discrete unnecessarily
**Example:** Age groups instead of exact ages
**Solution:** Keep original precision when possible

## 🛠️ Data Type Conversion Strategies

### Text to Numerical
\`\`\`python
# Example: Convert ratings to numbers
rating_map = {
    'Poor': 1,
    'Fair': 2, 
    'Good': 3,
    'Excellent': 4
}
df['rating_numeric'] = df['rating'].map(rating_map)
\`\`\`

### Categorical Encoding
\`\`\`python
# One-hot encoding for nominal categories
pd.get_dummies(df['color'])

# Ordinal encoding for ordered categories
df['size_encoded'] = df['size'].map({
    'Small': 1, 'Medium': 2, 'Large': 3, 'XL': 4
})
\`\`\`

### Date/Time Processing
\`\`\`python
# Extract features from dates
df['year'] = pd.to_datetime(df['date']).dt.year
df['month'] = pd.to_datetime(df['date']).dt.month
df['day_of_week'] = pd.to_datetime(df['date']).dt.dayofweek
\`\`\`

## 📊 Choosing the Right Format for Your Project

### Small Data (< 1GB)
- **CSV** for simple tabular data
- **Excel** for business stakeholders
- **JSON** for nested/hierarchical data

### Medium Data (1GB - 100GB)  
- **Parquet** for analytics
- **HDF5** for scientific data
- **Database** for transactional workloads

### Big Data (> 100GB)
- **Distributed formats** (Parquet on HDFS)
- **Columnar stores** (BigQuery, Redshift)
- **Streaming formats** (Avro, Protocol Buffers)

## 🎯 Best Practices for Data Format Selection

### Consider Your Workflow
1. **Data Source**: What format does it come in?
2. **Processing Tools**: What does your ML library prefer?
3. **Team Skills**: What formats can your team handle?
4. **Performance Needs**: Speed vs storage vs compatibility?

### Plan for Scale
- Start with simple formats (CSV, JSON)
- Migrate to efficient formats (Parquet, HDF5) as data grows
- Consider cloud-native formats for distributed processing

### Document Everything
- What does each column mean?
- What are the valid values?
- How was missing data handled?
- When was the data last updated?

## 🏆 Key Takeaways

✅ **Data types determine preprocessing strategies**  
✅ **Choose formats based on data size and use case**  
✅ **Preserve information during type conversions**  
✅ **Document your data types and formats clearly**  
✅ **Consider performance and compatibility early**  
✅ **Plan for data growth and evolution**

Understanding data types isn't just academic—it directly impacts your ML success. The right type classification and format choice can make the difference between a model that works and one that fails spectacularly!

*Next up: We'll get our hands dirty cleaning real messy data...*`,
    lesson_type: 'interactive',
    order_index: 3,
    estimated_duration_minutes: 25,
    video_url: null,
    resources: {
      interactiveType: 'data-type-classification',
      examples: [
        { data: 'Customer ages: 23, 45, 67, 34, 52', correctType: 'numerical_continuous', explanation: 'Ages can be any real number within a range' },
        { data: 'Product ratings: Excellent, Good, Fair, Poor', correctType: 'categorical_ordinal', explanation: 'Clear ranking order from Poor to Excellent' },
        { data: 'Customer reviews: This product is amazing! I love...', correctType: 'unstructured_text', explanation: 'Free-form text content with no fixed structure' },
        { data: 'Purchase amounts: $23.45, $156.78, $45.00', correctType: 'numerical_continuous', explanation: 'Monetary values can have any decimal precision' },
        { data: 'Zip codes: 90210, 10001, 60601', correctType: 'categorical_nominal', explanation: 'Numbers representing categories, not quantities' },
        { data: 'Movie genres: Comedy, Drama, Action, Horror', correctType: 'categorical_nominal', explanation: 'Categories with no inherent order' },
        { data: 'Satisfaction scores: 1, 2, 3, 4, 5', correctType: 'categorical_ordinal', explanation: 'Clear scale from low to high satisfaction' },
        { data: 'Email addresses: user@email.com, customer@company.org', correctType: 'categorical_nominal', explanation: 'Text identifiers with no mathematical meaning' }
      ],
      types: [
        'numerical_continuous',
        'numerical_discrete',
        'categorical_nominal', 
        'categorical_ordinal',
        'unstructured_text'
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 20,
    created_at: '2024-02-01T15:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-4',
    course_id: '3',
    title: 'Cleaning Messy Data',
    description: 'Master the essential skills of data cleaning with hands-on Python exercises.',
    content: `# Cleaning Messy Data: The Foundation of Great Models

Real-world data is messy! In fact, data scientists spend 60-80% of their time cleaning data. Let's master the essential data cleaning skills that separate professional practitioners from hobbyists. 🧹✨

## 🎯 Why Data Cleaning Matters

**"Garbage in, garbage out"** isn't just a catchy phrase—it's a fundamental law of machine learning.

### The Cost of Dirty Data
- **Poor model performance:** Inaccurate predictions
- **Biased results:** Systematic errors that affect fairness  
- **Wasted resources:** Time and compute on bad foundations
- **Failed deployments:** Models that work in testing but fail in production

### Real-World Impact
- **Netflix recommendations:** Poor data quality leads to customer churn
- **Medical diagnosis:** Dirty data can literally be life-threatening
- **Financial trading:** Bad data causes million-dollar losses
- **Autonomous vehicles:** Sensor data errors cause accidents

## 🔍 Common Data Quality Issues

### 1. Missing Values 🕳️
**The Problem:** Gaps in your data that algorithms can't handle

**Types of Missing Data:**
- **MCAR (Missing Completely at Random):** Truly random gaps
- **MAR (Missing at Random):** Missing depends on other observed variables
- **MNAR (Missing Not at Random):** Missing is related to the missing value itself

**Examples:**
- Survey respondents skipping income questions (MNAR)
- Sensor failures causing random measurement gaps (MCAR)
- Younger people not providing phone numbers (MAR)

### 2. Duplicate Records 🔁
**The Problem:** Same entity appears multiple times

**Causes:**
- Data entry errors
- System glitches during data collection
- Merging datasets from different sources
- User creating multiple accounts

### 3. Inconsistent Formats 📏
**The Problem:** Same information stored in different ways

**Examples:**
- Dates: "2024-03-15", "March 15, 2024", "15/03/24"
- Names: "John Smith", "Smith, John", "J. Smith"
- Addresses: "123 Main St", "123 Main Street", "123 MAIN ST"
- Phone: "(555) 123-4567", "555-123-4567", "5551234567"

### 4. Outliers and Errors 📊
**The Problem:** Values that don't make sense or are extremely unusual

**Examples:**
- Age: 150 years (data entry error)
- Salary: $1 (probably missing units)
- Temperature: -500°F (sensor malfunction)
- Purchase amount: -$100 (refund coded incorrectly)

### 5. Encoding Issues 🔤
**The Problem:** Character encoding problems causing garbled text

**Examples:**
- Names with special characters: "José" becomes "Jos"
- Currency symbols: "€100" becomes "â‚¬100"
- Emojis in social media data appearing as question marks

## 🛠️ Your Data Cleaning Toolkit

### Essential Libraries
\`\`\`python
import pandas as pd
import numpy as np
import matplotlib.pyplot as plt
import seaborn as sns
from datetime import datetime
import re  # Regular expressions for text cleaning
\`\`\`

### Quick Data Quality Assessment
\`\`\`python
def data_quality_report(df):
    """Generate comprehensive data quality report"""
    print("=== DATA QUALITY REPORT ===")
    print(f"Dataset shape: {df.shape}")
    print(f"Memory usage: {df.memory_usage(deep=True).sum() / 1024**2:.1f} MB\\n")
    
    # Missing values
    missing = df.isnull().sum()
    missing_pct = (missing / len(df)) * 100
    
    print("Missing Values:")
    for col, count in missing[missing > 0].items():
        print(f"  {col}: {count} ({missing_pct[col]:.1f}%)")
    
    # Duplicates
    duplicates = df.duplicated().sum()
    print(f"\\nDuplicate rows: {duplicates} ({duplicates/len(df)*100:.1f}%)")
    
    # Data types
    print("\\nData types:")
    print(df.dtypes.value_counts())
    
    # Potential outliers (for numeric columns)
    numeric_cols = df.select_dtypes(include=[np.number]).columns
    print(f"\\nNumeric columns with potential outliers:")
    for col in numeric_cols:
        Q1, Q3 = df[col].quantile([0.25, 0.75])
        IQR = Q3 - Q1
        outliers = df[(df[col] < Q1 - 1.5*IQR) | (df[col] > Q3 + 1.5*IQR)]
        if len(outliers) > 0:
            print(f"  {col}: {len(outliers)} potential outliers")

# Usage
data_quality_report(your_dataframe)
\`\`\`

## 🧹 Hands-On Cleaning Techniques

### 1. Handling Missing Values

#### Strategy Selection
\`\`\`python
# Analyze missing patterns
import missingno as msno
msno.matrix(df)  # Visualize missing patterns
msno.heatmap(df)  # Correlation of missingness

# Different strategies for different scenarios
def handle_missing_values(df, column, strategy='auto'):
    if strategy == 'auto':
        missing_pct = df[column].isnull().mean()
        
        if missing_pct > 0.5:
            print(f"WARNING: {column} has {missing_pct:.1%} missing - consider dropping")
            return df.drop(columns=[column])
        elif missing_pct > 0.1:
            print(f"{column}: High missingness - using mode/median")
            if df[column].dtype == 'object':
                return df[column].fillna(df[column].mode()[0])
            else:
                return df[column].fillna(df[column].median())
        else:
            print(f"{column}: Low missingness - using mean/mode")
            if df[column].dtype == 'object':
                return df[column].fillna(df[column].mode()[0])
            else:
                return df[column].fillna(df[column].mean())
    
    # Specific strategies
    elif strategy == 'drop':
        return df.dropna(subset=[column])
    elif strategy == 'forward_fill':
        return df[column].fillna(method='ffill')
    elif strategy == 'interpolate':
        return df[column].interpolate()
\`\`\`

#### Advanced Missing Value Techniques
\`\`\`python
# Multiple imputation for more accurate filling
from sklearn.experimental import enable_iterative_imputer
from sklearn.impute import IterativeImputer

# Use other columns to predict missing values
imputer = IterativeImputer(random_state=42)
df_imputed = pd.DataFrame(
    imputer.fit_transform(df.select_dtypes(include=[np.number])),
    columns=df.select_dtypes(include=[np.number]).columns
)
\`\`\`

### 2. Removing Duplicates

#### Smart Duplicate Detection
\`\`\`python
# Simple exact duplicates
df.drop_duplicates(inplace=True)

# Duplicates based on specific columns
df.drop_duplicates(subset=['customer_id', 'date'], inplace=True)

# Fuzzy duplicate detection for names/addresses
from fuzzywuzzy import fuzz

def find_fuzzy_duplicates(df, column, threshold=90):
    """Find similar text entries that might be duplicates"""
    duplicates = []
    values = df[column].dropna().unique()
    
    for i, val1 in enumerate(values):
        for val2 in values[i+1:]:
            similarity = fuzz.ratio(str(val1), str(val2))
            if similarity >= threshold:
                duplicates.append((val1, val2, similarity))
    
    return duplicates

# Find potential name duplicates
name_dupes = find_fuzzy_duplicates(df, 'customer_name', threshold=85)
\`\`\`

### 3. Standardizing Formats

#### Date Standardization
\`\`\`python
def clean_dates(date_series):
    """Standardize various date formats"""
    # Common date patterns
    patterns = [
        '%Y-%m-%d',      # 2024-03-15
        '%m/%d/%Y',      # 03/15/2024  
        '%d/%m/%Y',      # 15/03/2024
        '%B %d, %Y',     # March 15, 2024
        '%b %d, %Y',     # Mar 15, 2024
    ]
    
    standardized = []
    for date_str in date_series:
        if pd.isna(date_str):
            standardized.append(np.nan)
            continue
            
        for pattern in patterns:
            try:
                parsed_date = datetime.strptime(str(date_str), pattern)
                standardized.append(parsed_date)
                break
            except ValueError:
                continue
        else:
            # If no pattern matches, try pandas auto-parsing
            try:
                standardized.append(pd.to_datetime(date_str))
            except:
                standardized.append(np.nan)
    
    return pd.Series(standardized)

# Apply date cleaning
df['clean_date'] = clean_dates(df['messy_date_column'])
\`\`\`

#### Text Standardization
\`\`\`python
def clean_text(text_series):
    """Comprehensive text cleaning"""
    def clean_single_text(text):
        if pd.isna(text):
            return text
            
        # Convert to string and lowercase
        text = str(text).lower()
        
        # Remove extra whitespace
        text = re.sub(r'\\s+', ' ', text).strip()
        
        # Standardize common abbreviations
        abbreviations = {
            'st\\.?': 'street',
            'ave\\.?': 'avenue', 
            'dr\\.?': 'drive',
            'blvd\\.?': 'boulevard',
            'inc\\.?': 'incorporated',
            'llc\\.?': 'llc',
            '&': 'and'
        }
        
        for abbrev, full in abbreviations.items():
            text = re.sub(abbrev, full, text)
        
        return text
    
    return text_series.apply(clean_single_text)

# Clean company names
df['clean_company'] = clean_text(df['company_name'])
\`\`\`

### 4. Outlier Detection and Treatment

#### Statistical Outlier Detection
\`\`\`python
def detect_outliers(df, column, method='iqr'):
    """Detect outliers using various methods"""
    
    if method == 'iqr':
        Q1 = df[column].quantile(0.25)
        Q3 = df[column].quantile(0.75)
        IQR = Q3 - Q1
        lower_bound = Q1 - 1.5 * IQR
        upper_bound = Q3 + 1.5 * IQR
        
    elif method == 'zscore':
        mean = df[column].mean()
        std = df[column].std()
        lower_bound = mean - 3 * std
        upper_bound = mean + 3 * std
        
    elif method == 'percentile':
        lower_bound = df[column].quantile(0.01)
        upper_bound = df[column].quantile(0.99)
    
    outliers = df[(df[column] < lower_bound) | (df[column] > upper_bound)]
    return outliers, lower_bound, upper_bound

# Find and handle outliers
outliers, lower, upper = detect_outliers(df, 'price', method='iqr')
print(f"Found {len(outliers)} outliers in price column")

# Treatment options:
# 1. Remove outliers
df_no_outliers = df[(df['price'] >= lower) & (df['price'] <= upper)]

# 2. Cap outliers
df['price_capped'] = df['price'].clip(lower=lower, upper=upper)

# 3. Transform to reduce impact
df['price_log'] = np.log1p(df['price'])  # Log transformation
\`\`\`

## 💻 Interactive Coding Exercise: Complete Data Cleaning Pipeline

**Your Mission:** Clean this messy customer dataset step by step!

\`\`\`python
import pandas as pd
import numpy as np
import re

# Messy customer dataset
messy_data = {
    'customer_id': [1, 2, 3, 3, 4, 5, 6, 7, 8, 9],
    'name': ['John Smith', 'jane doe', '  Bob Johnson  ', 'Bob Johnson', 
             'ALICE BROWN', 'charlie wilson', None, 'David Lee', 'Emma Davis', 'Frank Miller'],
    'email': ['john@email.com', 'JANE@EMAIL.COM', 'bob@company.org', 'bob@company.org',
              'alice@test.com', 'charlie@email', 'david@email.com', 'david@email.com', 
              'emma@email.com', 'frank@email.com'],
    'age': [25, 30, -5, 35, 40, 999, 28, 32, None, 29],
    'income': [50000, 75000, 60000, 60000, 85000, 95000, None, 70000, 80000, 65000],
    'join_date': ['2024-01-15', '01/15/2024', '2024/01/20', '2024-01-20', 
                  'January 25, 2024', '2024-02-01', '2024-02-05', '2024-02-10',
                  '2024-02-15', '2024-02-20']
}

df = pd.DataFrame(messy_data)
print("Original messy data:")
print(df)
print("\\n" + "="*50)

# TODO: Your data cleaning pipeline
# Step 1: Handle duplicates
# Hint: Use drop_duplicates() considering business logic

# Step 2: Clean names
# Hint: Standardize capitalization and whitespace

# Step 3: Validate and clean email addresses
# Hint: Check for valid email format, standardize case

# Step 4: Fix age outliers  
# Hint: Replace impossible ages with reasonable values

# Step 5: Handle missing income values
# Hint: Use appropriate imputation strategy

# Step 6: Standardize date formats
# Hint: Convert all dates to consistent format

print("\\nCleaned data:")
print(df_cleaned)
\`\`\`

### Solution Approach:
\`\`\`python
# Complete cleaning pipeline
def clean_customer_data(df):
    df_clean = df.copy()
    
    # 1. Handle exact duplicates (customer_id 3)
    df_clean = df_clean.drop_duplicates(subset=['customer_id'], keep='first')
    
    # 2. Clean names
    df_clean['name'] = df_clean['name'].str.strip().str.title()
    
    # 3. Clean emails
    df_clean['email'] = df_clean['email'].str.lower()
    # Remove invalid emails
    email_pattern = r'^[\\w\\.-]+@[\\w\\.-]+\\.[a-zA-Z]{2,}$'
    df_clean = df_clean[df_clean['email'].str.match(email_pattern, na=False)]
    
    # 4. Fix age outliers
    df_clean.loc[df_clean['age'] < 0, 'age'] = np.nan  # Negative age
    df_clean.loc[df_clean['age'] > 120, 'age'] = np.nan  # Impossible age
    df_clean['age'].fillna(df_clean['age'].median(), inplace=True)
    
    # 5. Handle missing income
    df_clean['income'].fillna(df_clean['income'].median(), inplace=True)
    
    # 6. Standardize dates
    df_clean['join_date'] = pd.to_datetime(df_clean['join_date'])
    
    return df_clean

df_cleaned = clean_customer_data(df)
\`\`\`

## 🏭 Production-Ready Cleaning Pipeline

### Automated Data Quality Monitoring
\`\`\`python
class DataQualityMonitor:
    def __init__(self, df):
        self.df = df
        self.quality_report = {}
    
    def check_missing_values(self, threshold=0.1):
        """Flag columns with excessive missing values"""
        missing_pct = self.df.isnull().mean()
        problematic_cols = missing_pct[missing_pct > threshold]
        self.quality_report['high_missing'] = problematic_cols.to_dict()
        return problematic_cols
    
    def check_duplicates(self):
        """Check for duplicate records"""
        duplicate_count = self.df.duplicated().sum()
        self.quality_report['duplicates'] = duplicate_count
        return duplicate_count
    
    def check_outliers(self, columns=None):
        """Detect outliers in numeric columns"""
        if columns is None:
            columns = self.df.select_dtypes(include=[np.number]).columns
            
        outlier_summary = {}
        for col in columns:
            Q1, Q3 = self.df[col].quantile([0.25, 0.75])
            IQR = Q3 - Q1
            outliers = self.df[(self.df[col] < Q1 - 1.5*IQR) | 
                              (self.df[col] > Q3 + 1.5*IQR)]
            outlier_summary[col] = len(outliers)
        
        self.quality_report['outliers'] = outlier_summary
        return outlier_summary
    
    def generate_report(self):
        """Generate comprehensive quality report"""
        self.check_missing_values()
        self.check_duplicates() 
        self.check_outliers()
        return self.quality_report

# Usage
monitor = DataQualityMonitor(df)
quality_report = monitor.generate_report()
print("Data Quality Report:", quality_report)
\`\`\`

## 🚨 Data Cleaning Best Practices

### 1. Always Backup Your Original Data ✅
\`\`\`python
# Create backup before cleaning
df_backup = df.copy()
# Or save to file
df.to_csv('data_backup.csv', index=False)
\`\`\`

### 2. Document Your Cleaning Steps ✅
\`\`\`python
cleaning_log = {
    'step_1': 'Removed 15 duplicate records based on customer_id',
    'step_2': 'Filled missing ages with median value (32 years)', 
    'step_3': 'Standardized all email addresses to lowercase',
    'step_4': 'Converted 3 date formats to YYYY-MM-DD standard'
}
\`\`\`

### 3. Validate Cleaning Results ✅
\`\`\`python
def validate_cleaning(df_before, df_after):
    """Validate that cleaning didn't introduce errors"""
    print(f"Rows before: {len(df_before)}, after: {len(df_after)}")
    print(f"Columns before: {len(df_before.columns)}, after: {len(df_after.columns)}")
    
    # Check for unexpected changes
    for col in df_before.columns:
        if col in df_after.columns:
            before_unique = df_before[col].nunique()
            after_unique = df_after[col].nunique()
            if abs(before_unique - after_unique) > before_unique * 0.1:
                print(f"WARNING: {col} unique values changed significantly")

validate_cleaning(df, df_cleaned)
\`\`\`

### 4. Make Cleaning Reproducible ✅
\`\`\`python
def clean_data_pipeline(input_file, output_file):
    """Reproducible data cleaning pipeline"""
    # Load data
    df = pd.read_csv(input_file)
    
    # Apply cleaning steps
    df = remove_duplicates(df)
    df = handle_missing_values(df)
    df = standardize_formats(df)
    df = detect_and_treat_outliers(df)
    
    # Save cleaned data
    df.to_csv(output_file, index=False)
    
    # Log cleaning summary
    with open(f"{output_file}_cleaning_log.txt", 'w') as f:
        f.write(f"Cleaned data saved to {output_file}\\n")
        f.write(f"Original rows: {original_rows}\\n")
        f.write(f"Final rows: {len(df)}\\n")
    
    return df
\`\`\`

## 🎯 Key Takeaways

✅ **Data cleaning is iterative** - expect multiple rounds of refinement  
✅ **Domain knowledge is crucial** - understand what values make sense  
✅ **Document everything** - future you will thank present you  
✅ **Validate your results** - cleaning shouldn't introduce new errors  
✅ **Automate when possible** - build reusable cleaning pipelines  
✅ **Quality over quantity** - better to have less clean data than more dirty data

Remember: **No model can overcome fundamentally bad data.** Time spent on thorough data cleaning is the best investment you can make in your ML project's success!

*Next: We'll learn to explore and visualize our clean data to uncover hidden insights...*`,
    lesson_type: 'coding',
    order_index: 4,
    estimated_duration_minutes: 40,
    video_url: null,
    resources: {
      starterCode: `# Complete Data Cleaning Pipeline Exercise
import pandas as pd
import numpy as np
import re
from datetime import datetime

# Messy customer dataset
messy_data = {
    'customer_id': [1, 2, 3, 3, 4, 5, 6, 7, 8, 9],
    'name': ['John Smith', 'jane doe', '  Bob Johnson  ', 'Bob Johnson', 
             'ALICE BROWN', 'charlie wilson', None, 'David Lee', 'Emma Davis', 'Frank Miller'],
    'email': ['john@email.com', 'JANE@EMAIL.COM', 'bob@company.org', 'bob@company.org',
              'alice@test.com', 'charlie@email', 'david@email.com', 'david@email.com', 
              'emma@email.com', 'frank@email.com'],
    'age': [25, 30, -5, 35, 40, 999, 28, 32, None, 29],
    'income': [50000, 75000, 60000, 60000, 85000, 95000, None, 70000, 80000, 65000],
    'join_date': ['2024-01-15', '01/15/2024', '2024/01/20', '2024-01-20', 
                  'January 25, 2024', '2024-02-01', '2024-02-05', '2024-02-10',
                  '2024-02-15', '2024-02-20']
}

df = pd.DataFrame(messy_data)
print("Original messy data:")
print(df)
print("\\nData quality issues to fix:")
print("1. Duplicate customer_id (3 appears twice)")
print("2. Inconsistent name formatting")  
print("3. Invalid email addresses and mixed case")
print("4. Impossible age values (-5, 999)")
print("5. Missing values in name, age, income")
print("6. Inconsistent date formats")

# TODO: Implement your data cleaning pipeline
def clean_customer_data(df):
    df_clean = df.copy()
    
    # Step 1: TODO - Handle duplicates
    # Hint: Use drop_duplicates() on customer_id
    
    # Step 2: TODO - Clean names (remove extra spaces, proper capitalization)
    # Hint: Use str.strip() and str.title()
    
    # Step 3: TODO - Clean emails (lowercase, validate format)
    # Hint: Use str.lower() and regex to validate emails
    
    # Step 4: TODO - Fix impossible ages
    # Hint: Set ages < 0 or > 120 to NaN, then fill with median
    
    # Step 5: TODO - Handle missing income
    # Hint: Fill with median income
    
    # Step 6: TODO - Standardize date formats
    # Hint: Use pd.to_datetime()
    
    return df_clean

# Apply your cleaning pipeline
df_cleaned = clean_customer_data(df)

print("\\nCleaned data:")
print(df_cleaned)
print("\\nValidation:")
print(f"Shape before: {df.shape}, after: {df_cleaned.shape}")
print(f"Missing values before: {df.isnull().sum().sum()}, after: {df_cleaned.isnull().sum().sum()}")
`,
      expectedOutput: "Original messy data processed successfully\\nCleaned data shape: (8, 6)\\nNo missing values remaining\\nAll data quality issues resolved"
    },
    is_preview: false,
    is_required: true,
    points_value: 30,
    created_at: '2024-02-01T16:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-5',
    course_id: '3',
    title: 'Exploring Data with Visuals',
    description: 'Learn to uncover insights and patterns in your data through effective visualization.',
    content: `# Exploring Data with Visuals: Seeing Patterns in Numbers

A picture is worth a thousand rows! Data visualization is your superpower for understanding patterns, spotting problems, and communicating insights. Let's master the art of visual data exploration! 📊✨

## 🎯 Why Visualization is Essential for ML

### The Human Visual Advantage
- **Pattern Recognition:** Humans excel at spotting visual patterns
- **Outlier Detection:** Abnormal points jump out in charts
- **Relationship Discovery:** Correlations become obvious visually
- **Communication:** Stakeholders understand pictures better than statistics

### Famous Data Visualization Success Stories

**Anscombe's Quartet (1973)**
Four datasets with identical statistics but completely different patterns:
- All have same mean, standard deviation, and correlation
- Visualization reveals they're totally different!
- **Lesson:** Always plot your data—statistics can lie!

**John Snow's Cholera Map (1854)**
- Mapped cholera deaths in London
- Revealed disease source was contaminated water pump
- Visualization literally saved lives!

## 📊 The Data Exploration Workflow

### 1. Know Your Data First 🔍
\`\`\`python
import pandas as pd
import matplotlib.pyplot as plt
import seaborn as sns
import numpy as np

# Set style for better-looking plots
plt.style.use('seaborn-v0_8')
sns.set_palette("husl")

# Initial data inspection
def explore_dataset(df):
    print("=== DATASET OVERVIEW ===")
    print(f"Shape: {df.shape}")
    print(f"Columns: {list(df.columns)}")
    print(f"\\nData types:\\n{df.dtypes}")
    print(f"\\nMemory usage: {df.memory_usage(deep=True).sum()/1024**2:.1f} MB")
    
    # Basic statistics
    print(f"\\n=== NUMERICAL SUMMARY ===")
    print(df.describe())
    
    # Missing values
    missing = df.isnull().sum()
    if missing.any():
        print(f"\\n=== MISSING VALUES ===")
        print(missing[missing > 0])
\`\`\`

### 2. Univariate Analysis (One Variable at a Time) 📈

#### Numerical Variables
\`\`\`python
def plot_numerical_distribution(df, column):
    """Comprehensive analysis of a numerical column"""
    fig, axes = plt.subplots(2, 2, figsize=(12, 8))
    fig.suptitle(f'Distribution Analysis: {column}', fontsize=16)
    
    # Histogram
    axes[0,0].hist(df[column].dropna(), bins=30, alpha=0.7, edgecolor='black')
    axes[0,0].set_title('Histogram')
    axes[0,0].set_xlabel(column)
    axes[0,0].set_ylabel('Frequency')
    
    # Box plot
    axes[0,1].boxplot(df[column].dropna())
    axes[0,1].set_title('Box Plot')
    axes[0,1].set_ylabel(column)
    
    # Q-Q plot (check if data is normally distributed)
    from scipy import stats
    stats.probplot(df[column].dropna(), dist="norm", plot=axes[1,0])
    axes[1,0].set_title('Q-Q Plot (Normality Check)')
    
    # Density plot
    df[column].dropna().plot(kind='density', ax=axes[1,1])
    axes[1,1].set_title('Density Plot')
    axes[1,1].set_xlabel(column)
    
    plt.tight_layout()
    plt.show()
    
    # Statistical insights
    data = df[column].dropna()
    print(f"\\n=== {column.upper()} INSIGHTS ===")
    print(f"Count: {len(data):,}")
    print(f"Mean: {data.mean():.2f}")
    print(f"Median: {data.median():.2f}")
    print(f"Mode: {data.mode().iloc[0] if len(data.mode()) > 0 else 'N/A'}")
    print(f"Std Dev: {data.std():.2f}")
    print(f"Skewness: {data.skew():.2f}")
    print(f"Range: {data.min():.2f} to {data.max():.2f}")

# Example usage
plot_numerical_distribution(df, 'price')
\`\`\`

#### Categorical Variables
\`\`\`python
def plot_categorical_distribution(df, column):
    """Analyze categorical variable distribution"""
    fig, axes = plt.subplots(1, 2, figsize=(12, 5))
    fig.suptitle(f'Category Analysis: {column}', fontsize=16)
    
    # Bar plot
    value_counts = df[column].value_counts()
    axes[0].bar(range(len(value_counts)), value_counts.values)
    axes[0].set_xticks(range(len(value_counts)))
    axes[0].set_xticklabels(value_counts.index, rotation=45)
    axes[0].set_title('Frequency Count')
    axes[0].set_ylabel('Count')
    
    # Pie chart
    axes[1].pie(value_counts.values, labels=value_counts.index, autopct='%1.1f%%')
    axes[1].set_title('Proportion')
    
    plt.tight_layout()
    plt.show()
    
    # Category insights
    print(f"\\n=== {column.upper()} INSIGHTS ===")
    print(f"Unique categories: {df[column].nunique()}")
    print(f"Most common: {value_counts.index[0]} ({value_counts.iloc[0]:,} occurrences)")
    print(f"Least common: {value_counts.index[-1]} ({value_counts.iloc[-1]:,} occurrences)")
    print(f"Distribution balance: {value_counts.min()/value_counts.max():.2f} (1.0 = perfectly balanced)")

# Example usage  
plot_categorical_distribution(df, 'category')
\`\`\`

### 3. Bivariate Analysis (Two Variables) 🔗

#### Numerical vs Numerical
\`\`\`python
def analyze_numerical_relationship(df, x_col, y_col):
    """Explore relationship between two numerical variables"""
    fig, axes = plt.subplots(2, 2, figsize=(12, 8))
    fig.suptitle(f'Relationship: {x_col} vs {y_col}', fontsize=16)
    
    # Scatter plot
    axes[0,0].scatter(df[x_col], df[y_col], alpha=0.6)
    axes[0,0].set_xlabel(x_col)
    axes[0,0].set_ylabel(y_col)
    axes[0,0].set_title('Scatter Plot')
    
    # Add correlation coefficient
    correlation = df[x_col].corr(df[y_col])
    axes[0,0].text(0.05, 0.95, f'Correlation: {correlation:.2f}', 
                   transform=axes[0,0].transAxes, 
                   bbox=dict(boxstyle="round", facecolor='wheat'))
    
    # Hexbin plot for dense data
    axes[0,1].hexbin(df[x_col], df[y_col], gridsize=20, cmap='Blues')
    axes[0,1].set_xlabel(x_col) 
    axes[0,1].set_ylabel(y_col)
    axes[0,1].set_title('Hexbin Plot (Density)')
    
    # Joint distribution with regression line
    sns.regplot(data=df, x=x_col, y=y_col, ax=axes[1,0])
    axes[1,0].set_title('Regression Line')
    
    # Residual plot
    from sklearn.linear_model import LinearRegression
    model = LinearRegression()
    X = df[x_col].values.reshape(-1, 1)
    y = df[y_col].values
    
    # Remove NaN values
    mask = ~(np.isnan(X.flatten()) | np.isnan(y))
    X_clean, y_clean = X[mask], y[mask]
    
    model.fit(X_clean, y_clean)
    residuals = y_clean - model.predict(X_clean)
    
    axes[1,1].scatter(X_clean, residuals, alpha=0.6)
    axes[1,1].axhline(y=0, color='red', linestyle='--')
    axes[1,1].set_xlabel(x_col)
    axes[1,1].set_ylabel('Residuals')
    axes[1,1].set_title('Residual Plot')
    
    plt.tight_layout()
    plt.show()
    
    # Relationship insights
    print(f"\\n=== RELATIONSHIP INSIGHTS ===")
    print(f"Correlation: {correlation:.3f}")
    if abs(correlation) > 0.7:
        strength = "Strong"
    elif abs(correlation) > 0.3:
        strength = "Moderate" 
    else:
        strength = "Weak"
    
    direction = "positive" if correlation > 0 else "negative"
    print(f"Relationship: {strength} {direction} correlation")

# Example usage
analyze_numerical_relationship(df, 'budget', 'rating')
\`\`\`

#### Categorical vs Numerical  
\`\`\`python
def analyze_categorical_numerical(df, cat_col, num_col):
    """Explore how categorical variable affects numerical variable"""
    fig, axes = plt.subplots(2, 2, figsize=(12, 8))
    fig.suptitle(f'{num_col} by {cat_col}', fontsize=16)
    
    # Box plot
    df.boxplot(column=num_col, by=cat_col, ax=axes[0,0])
    axes[0,0].set_title('Box Plot Comparison')
    axes[0,0].set_xlabel(cat_col)
    
    # Violin plot
    sns.violinplot(data=df, x=cat_col, y=num_col, ax=axes[0,1])
    axes[0,1].set_title('Violin Plot (Distribution Shape)')
    axes[0,1].tick_params(axis='x', rotation=45)
    
    # Bar plot of means
    means = df.groupby(cat_col)[num_col].mean()
    axes[1,0].bar(range(len(means)), means.values)
    axes[1,0].set_xticks(range(len(means)))
    axes[1,0].set_xticklabels(means.index, rotation=45)
    axes[1,0].set_title('Average by Category')
    axes[1,0].set_ylabel(f'Average {num_col}')
    
    # Strip plot (all individual points)
    sns.stripplot(data=df, x=cat_col, y=num_col, ax=axes[1,1], alpha=0.6)
    axes[1,1].set_title('Individual Points')
    axes[1,1].tick_params(axis='x', rotation=45)
    
    plt.tight_layout()
    plt.show()
    
    # Statistical analysis
    categories = df[cat_col].unique()
    print(f"\\n=== {num_col.upper()} BY {cat_col.upper()} ===")
    
    for cat in categories:
        subset = df[df[cat_col] == cat][num_col]
        print(f"{cat}: Mean={subset.mean():.2f}, Std={subset.std():.2f}, Count={len(subset)}")
    
    # ANOVA test (statistical significance)
    from scipy.stats import f_oneway
    groups = [df[df[cat_col] == cat][num_col].dropna() for cat in categories]
    f_stat, p_value = f_oneway(*groups)
    
    print(f"\\nANOVA Test:")
    print(f"F-statistic: {f_stat:.2f}")
    print(f"P-value: {p_value:.4f}")
    if p_value < 0.05:
        print("✅ Significant difference between categories")
    else:
        print("❌ No significant difference between categories")

# Example usage
analyze_categorical_numerical(df, 'genre', 'rating')
\`\`\`

### 4. Multivariate Analysis (Multiple Variables) 🕸️

#### Correlation Heatmap
\`\`\`python
def plot_correlation_matrix(df):
    """Create comprehensive correlation analysis"""
    # Select only numerical columns
    numerical_cols = df.select_dtypes(include=[np.number]).columns
    
    if len(numerical_cols) < 2:
        print("Not enough numerical columns for correlation analysis")
        return
    
    # Calculate correlation matrix
    corr_matrix = df[numerical_cols].corr()
    
    # Create heatmap
    plt.figure(figsize=(10, 8))
    sns.heatmap(corr_matrix, 
                annot=True,           # Show correlation values
                cmap='RdBu_r',        # Color scheme
                center=0,             # Center colormap at 0
                square=True,          # Square cells
                fmt='.2f',            # 2 decimal places
                cbar_kws={'shrink': 0.8})
    
    plt.title('Feature Correlation Matrix', fontsize=16)
    plt.tight_layout()
    plt.show()
    
    # Find strongest correlations
    corr_pairs = []
    for i in range(len(corr_matrix.columns)):
        for j in range(i+1, len(corr_matrix.columns)):
            col1, col2 = corr_matrix.columns[i], corr_matrix.columns[j]
            corr_value = corr_matrix.iloc[i, j]
            corr_pairs.append((abs(corr_value), col1, col2, corr_value))
    
    # Sort by absolute correlation strength
    corr_pairs.sort(reverse=True)
    
    print("\\n=== STRONGEST CORRELATIONS ===")
    for abs_corr, col1, col2, corr in corr_pairs[:5]:
        direction = "positive" if corr > 0 else "negative"
        print(f"{col1} ↔ {col2}: {corr:.3f} ({direction})")

plot_correlation_matrix(df)
\`\`\`

#### Pairplot for Multiple Variables
\`\`\`python
def create_comprehensive_pairplot(df, hue_column=None):
    """Create pairplot with categorical coloring"""
    # Select subset of numerical columns (max 5 for readability)
    numerical_cols = df.select_dtypes(include=[np.number]).columns[:5]
    
    if len(numerical_cols) < 2:
        print("Need at least 2 numerical columns for pairplot")
        return
    
    # Create pairplot
    subset_df = df[list(numerical_cols) + ([hue_column] if hue_column else [])]
    
    g = sns.pairplot(subset_df, 
                     hue=hue_column,
                     diag_kind='hist',    # Histograms on diagonal
                     plot_kws={'alpha': 0.6},
                     height=2.5)
    
    g.fig.suptitle('Pairwise Relationships', y=1.02, fontsize=16)
    plt.tight_layout()
    plt.show()
    
    return g

# Example usage
create_comprehensive_pairplot(df, hue_column='category')
\`\`\`

## 🎮 Interactive Exercise: Chart Detective

**Your Mission:** Look at these charts and identify the insights! 

### Exercise 1: Distribution Analysis
\`\`\`python
# Generate sample data for the exercise
np.random.seed(42)
sample_data = {
    'normal_dist': np.random.normal(50, 15, 1000),
    'skewed_dist': np.random.gamma(2, 2, 1000) * 10,
    'bimodal_dist': np.concatenate([
        np.random.normal(30, 5, 500),
        np.random.normal(70, 5, 500)
    ]),
    'uniform_dist': np.random.uniform(0, 100, 1000)
}

fig, axes = plt.subplots(2, 2, figsize=(12, 8))
axes = axes.ravel()

for i, (name, data) in enumerate(sample_data.items()):
    axes[i].hist(data, bins=30, alpha=0.7, edgecolor='black')
    axes[i].set_title(f'Distribution {i+1}: {name}')
    axes[i].set_xlabel('Value')
    axes[i].set_ylabel('Frequency')

plt.tight_layout()
plt.show()

print("📊 QUIZ: What can you tell about each distribution?")
print("1. Which distribution is normal?")
print("2. Which is skewed to the right?") 
print("3. Which has two peaks (bimodal)?")
print("4. Which is uniform/flat?")
\`\`\`

**Solutions:**
1. **Normal:** Distribution 1 (bell-shaped, symmetric)
2. **Right-skewed:** Distribution 2 (tail extends to the right)
3. **Bimodal:** Distribution 3 (two distinct peaks)
4. **Uniform:** Distribution 4 (flat, equal frequencies)

### Exercise 2: Correlation Interpretation
\`\`\`python
# Create datasets with different correlation patterns
n = 200
x = np.random.randn(n)

correlations = {
    'Strong Positive': (x, 2*x + np.random.randn(n)*0.5),
    'Weak Negative': (x, -0.3*x + np.random.randn(n)*2),
    'No Correlation': (x, np.random.randn(n)),
    'Non-linear': (x, x**2 + np.random.randn(n)*0.5)
}

fig, axes = plt.subplots(2, 2, figsize=(12, 8))
axes = axes.ravel()

for i, (title, (x_data, y_data)) in enumerate(correlations.items()):
    correlation = np.corrcoef(x_data, y_data)[0, 1]
    
    axes[i].scatter(x_data, y_data, alpha=0.6)
    axes[i].set_title(f'{title}\\nCorrelation: {correlation:.2f}')
    axes[i].set_xlabel('X')
    axes[i].set_ylabel('Y')

plt.tight_layout()
plt.show()

print("🔍 ANALYSIS QUESTIONS:")
print("1. Which relationship would linear regression model best?")
print("2. Which shows that correlation ≠ causation?")
print("3. Which might need non-linear modeling?")
\`\`\`

## 📈 Advanced Visualization Techniques

### 1. Time Series Visualization
\`\`\`python
def plot_time_series(df, date_col, value_col):
    """Comprehensive time series analysis"""
    # Ensure datetime
    df[date_col] = pd.to_datetime(df[date_col])
    
    fig, axes = plt.subplots(3, 1, figsize=(12, 10))
    
    # Main time series
    axes[0].plot(df[date_col], df[value_col], linewidth=1)
    axes[0].set_title('Time Series Trend')
    axes[0].set_ylabel(value_col)
    
    # Rolling statistics
    rolling_mean = df[value_col].rolling(window=30).mean()
    rolling_std = df[value_col].rolling(window=30).std()
    
    axes[1].plot(df[date_col], rolling_mean, label='30-day Mean')
    axes[1].fill_between(df[date_col], 
                        rolling_mean - rolling_std,
                        rolling_mean + rolling_std,
                        alpha=0.3, label='±1 Std Dev')
    axes[1].set_title('Rolling Statistics')
    axes[1].legend()
    
    # Seasonality (if enough data)
    if len(df) > 365:
        df['month'] = df[date_col].dt.month
        monthly_avg = df.groupby('month')[value_col].mean()
        axes[2].plot(monthly_avg.index, monthly_avg.values, marker='o')
        axes[2].set_title('Seasonal Pattern')
        axes[2].set_xlabel('Month')
        axes[2].set_xticks(range(1, 13))
    
    plt.tight_layout()
    plt.show()
\`\`\`

### 2. Geographical Visualization
\`\`\`python
def plot_geographical_data(df, lat_col, lon_col, value_col):
    """Plot data on a map"""
    plt.figure(figsize=(12, 8))
    
    # Scatter plot with value-based coloring
    scatter = plt.scatter(df[lon_col], df[lat_col], 
                         c=df[value_col], 
                         cmap='viridis',
                         alpha=0.6,
                         s=50)
    
    plt.colorbar(scatter, label=value_col)
    plt.xlabel('Longitude')
    plt.ylabel('Latitude')
    plt.title(f'{value_col} by Geographic Location')
    
    # Add grid
    plt.grid(True, alpha=0.3)
    plt.tight_layout()
    plt.show()
\`\`\`

## 🎯 Visualization Best Practices

### 1. Choose the Right Chart Type ✅

**Bar Charts:** Comparing categories
**Line Charts:** Showing trends over time
**Scatter Plots:** Exploring relationships
**Histograms:** Understanding distributions
**Box Plots:** Comparing distributions across groups
**Heatmaps:** Showing correlations or patterns in 2D data

### 2. Design for Your Audience ✅

**For Technical Teams:**
- Include statistical details
- Use precise scales
- Show uncertainty/confidence intervals

**For Business Stakeholders:**
- Emphasize insights over details
- Use intuitive color schemes
- Add clear annotations

### 3. Avoid Common Mistakes ❌

**Don't:**
- Use 3D charts (they distort perception)
- Start bar charts at non-zero values (misleading)
- Use too many colors (confusing)
- Make charts too small (unreadable)
- Forget axis labels (unclear)

**Do:**
- Keep it simple and focused
- Use consistent color schemes
- Add clear titles and labels
- Show uncertainty when relevant
- Tell a story with your data

## 🏆 Key Takeaways

✅ **Always visualize before modeling** - patterns jump out visually  
✅ **Use multiple chart types** - each reveals different aspects  
✅ **Look for outliers and anomalies** - they might be data errors or valuable insights  
✅ **Consider your audience** - technical detail vs business insight  
✅ **Interactive exploration beats static reports** - dig deeper into interesting patterns  
✅ **Document your discoveries** - insights guide feature engineering and modeling

Remember: **The goal isn't pretty pictures—it's actionable insights!** Great visualizations lead to better models, clearer communication, and more successful ML projects.

*Next: We'll learn to engineer new features from our explored data to improve model performance...*`,
    lesson_type: 'interactive',
    order_index: 5,
    estimated_duration_minutes: 35,
    video_url: null,
    resources: {
      interactiveType: 'chart-interpretation',
      exercises: [
        {
          title: 'Distribution Detective',
          charts: ['normal', 'skewed', 'bimodal', 'uniform'],
          task: 'Match each distribution to its characteristics'
        },
        {
          title: 'Correlation Analysis',
          scenarios: [
            { description: 'Strong positive correlation (r=0.85)', answer: 'linear_relationship', chart_type: 'scatter' },
            { description: 'No correlation (r=0.02)', answer: 'no_pattern', chart_type: 'scatter' },
            { description: 'Non-linear relationship', answer: 'quadratic_pattern', chart_type: 'scatter' }
          ],
          task: 'Identify the relationship type from scatter plots'
        },
        {
          title: 'Best Chart Selection',
          scenarios: [
            { data_type: 'categorical_counts', best_chart: 'bar_chart' },
            { data_type: 'time_series', best_chart: 'line_chart' },
            { data_type: 'two_numerical', best_chart: 'scatter_plot' },
            { data_type: 'distribution_shape', best_chart: 'histogram' }
          ],
          task: 'Choose the most appropriate visualization for each data type'
        }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 25,
    created_at: '2024-02-01T17:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-6',
    course_id: '3',
    title: 'Feature Engineering Basics',
    description: 'Learn to create new features that improve your machine learning models.',
    content: `# Feature Engineering: Creating Better Inputs for Better Models

Feature engineering is where the magic happens! It's the art and science of transforming raw data into features that help machine learning algorithms perform their best. This skill often separates good ML practitioners from great ones! 🔧✨

## 🎯 What is Feature Engineering?

**Feature Engineering** = Creating new variables (features) from existing data to improve model performance

Think of it like being a chef:
- **Raw ingredients** = Original data columns
- **Preparation techniques** = Feature engineering methods  
- **Improved dish** = Better performing ML model

### Why It Matters More Than Algorithm Choice

**Real-world impact studies show:**
- 💡 Good features + simple algorithm > Poor features + complex algorithm
- 📈 Feature engineering can improve model performance by 20-50%
- 🎯 Domain experts who understand feature engineering often beat pure statisticians
- ⚡ Well-engineered features make models faster and more interpretable

## 🔧 The Feature Engineering Toolkit

### 1. Mathematical Transformations 🧮

#### Log Transformations
**When to use:** Highly skewed numerical data
**Effect:** Reduces skewness, handles wide value ranges

\`\`\`python
import numpy as np
import pandas as pd
import matplotlib.pyplot as plt

# Example: Income data (highly skewed)
incomes = np.array([25000, 30000, 35000, 45000, 50000, 75000, 100000, 500000, 1000000])

# Original vs log-transformed
fig, axes = plt.subplots(1, 2, figsize=(12, 4))

axes[0].hist(incomes, bins=20, alpha=0.7, edgecolor='black')
axes[0].set_title('Original Income Distribution')
axes[0].set_xlabel('Income ($)')

log_incomes = np.log1p(incomes)  # log1p = log(1 + x) to handle zeros
axes[1].hist(log_incomes, bins=20, alpha=0.7, edgecolor='black')
axes[1].set_title('Log-Transformed Income')
axes[1].set_xlabel('Log(Income)')

plt.tight_layout()
plt.show()

# Apply transformation
df['income_log'] = np.log1p(df['income'])
df['salary_log'] = np.log1p(df['salary'])
\`\`\`

#### Square Root & Power Transformations
\`\`\`python
# Moderate skewness reduction
df['price_sqrt'] = np.sqrt(df['price'])

# Custom power transformation
df['age_squared'] = df['age'] ** 2
df['age_cubed'] = df['age'] ** 3

# Box-Cox transformation (automatic optimal power)
from scipy import stats
df['sales_boxcox'], lambda_param = stats.boxcox(df['sales'] + 1)
print(f"Optimal lambda: {lambda_param:.3f}")
\`\`\`

#### Standardization & Normalization
\`\`\`python
from sklearn.preprocessing import StandardScaler, MinMaxScaler

# Z-score normalization (mean=0, std=1)
scaler = StandardScaler()
df['price_standardized'] = scaler.fit_transform(df[['price']])

# Min-Max scaling (range 0-1)
minmax_scaler = MinMaxScaler()
df['rating_normalized'] = minmax_scaler.fit_transform(df[['rating']])

# Robust scaling (uses median and IQR, less sensitive to outliers)
from sklearn.preprocessing import RobustScaler
robust_scaler = RobustScaler()
df['income_robust'] = robust_scaler.fit_transform(df[['income']])
\`\`\`

### 2. Categorical Encoding 🏷️

#### One-Hot Encoding
**Best for:** Nominal categories with few unique values (<10-20)

\`\`\`python
# Simple one-hot encoding
df_encoded = pd.get_dummies(df, columns=['category', 'color'], prefix=['cat', 'color'])

# More control with sklearn
from sklearn.preprocessing import OneHotEncoder

encoder = OneHotEncoder(sparse=False, drop='first')  # drop='first' prevents multicollinearity
encoded = encoder.fit_transform(df[['category']])
encoded_df = pd.DataFrame(encoded, columns=encoder.get_feature_names_out(['category']))

# Concatenate back to original data
df_final = pd.concat([df, encoded_df], axis=1)
\`\`\`

#### Ordinal Encoding
**Best for:** Ordered categories

\`\`\`python
# Manual ordinal mapping
education_map = {
    'High School': 1,
    'Some College': 2,
    'Bachelor': 3,
    'Master': 4,
    'PhD': 5
}
df['education_ordinal'] = df['education'].map(education_map)

# Size mapping
size_map = {'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, 'XXL': 6}
df['size_ordinal'] = df['size'].map(size_map)

# Sklearn ordinal encoder
from sklearn.preprocessing import OrdinalEncoder
ordinal_encoder = OrdinalEncoder(categories=[['Low', 'Medium', 'High']])
df['priority_ordinal'] = ordinal_encoder.fit_transform(df[['priority']])
\`\`\`

#### Target Encoding
**Best for:** High-cardinality categorical variables

\`\`\`python
def target_encode(df, categorical_col, target_col, smoothing=1):
    """
    Target encoding with smoothing to prevent overfitting
    """
    # Calculate global mean
    global_mean = df[target_col].mean()
    
    # Calculate category means and counts
    category_stats = df.groupby(categorical_col)[target_col].agg(['mean', 'count'])
    category_stats['smoothed_mean'] = (
        (category_stats['mean'] * category_stats['count'] + global_mean * smoothing) /
        (category_stats['count'] + smoothing)
    )
    
    # Map back to original data
    encoded_values = df[categorical_col].map(category_stats['smoothed_mean'])
    return encoded_values

# Example: Encode movie director based on average rating
df['director_rating_encoded'] = target_encode(df, 'director', 'rating', smoothing=10)
\`\`\`

### 3. Date & Time Features ⏰

#### Extracting Time Components
\`\`\`python
# Convert to datetime if not already
df['date'] = pd.to_datetime(df['date'])

# Extract basic components
df['year'] = df['date'].dt.year
df['month'] = df['date'].dt.month
df['day'] = df['date'].dt.day
df['dayofweek'] = df['date'].dt.dayofweek  # 0=Monday, 6=Sunday
df['quarter'] = df['date'].dt.quarter
df['week_of_year'] = df['date'].dt.isocalendar().week

# More advanced time features
df['is_weekend'] = df['dayofweek'].isin([5, 6]).astype(int)
df['is_month_start'] = df['date'].dt.is_month_start.astype(int)
df['is_month_end'] = df['date'].dt.is_month_end.astype(int)
df['is_quarter_start'] = df['date'].dt.is_quarter_start.astype(int)

# Cyclical encoding for periodic features
df['month_sin'] = np.sin(2 * np.pi * df['month'] / 12)
df['month_cos'] = np.cos(2 * np.pi * df['month'] / 12)
df['dayofweek_sin'] = np.sin(2 * np.pi * df['dayofweek'] / 7)
df['dayofweek_cos'] = np.cos(2 * np.pi * df['dayofweek'] / 7)
\`\`\`

#### Time-Based Features
\`\`\`python
# Sort by date first
df = df.sort_values('date')

# Lag features (previous values)
df['price_lag_1'] = df['price'].shift(1)  # Previous day
df['price_lag_7'] = df['price'].shift(7)  # Previous week
df['price_lag_30'] = df['price'].shift(30)  # Previous month

# Rolling statistics
df['price_rolling_mean_7'] = df['price'].rolling(window=7).mean()
df['price_rolling_std_7'] = df['price'].rolling(window=7).std()
df['price_rolling_min_7'] = df['price'].rolling(window=7).min()
df['price_rolling_max_7'] = df['price'].rolling(window=7).max()

# Time since events
reference_date = pd.to_datetime('2024-01-01')
df['days_since_reference'] = (df['date'] - reference_date).dt.days

# Age calculations
today = pd.Timestamp.now()
df['account_age_days'] = (today - df['created_date']).dt.days
df['account_age_years'] = df['account_age_days'] / 365.25
\`\`\`

### 4. Text Feature Engineering 📝

#### Basic Text Features
\`\`\`python
# Length-based features
df['review_length'] = df['review'].str.len()
df['word_count'] = df['review'].str.split().str.len()
df['sentence_count'] = df['review'].str.split('.').str.len()

# Readability features
df['avg_word_length'] = df['review'].apply(
    lambda x: np.mean([len(word) for word in x.split()]) if x else 0
)

# Punctuation features
df['exclamation_count'] = df['review'].str.count('!')
df['question_count'] = df['review'].str.count('\\?')
df['caps_ratio'] = df['review'].apply(
    lambda x: sum(1 for c in x if c.isupper()) / len(x) if x else 0
)

# Keyword presence
important_keywords = ['excellent', 'amazing', 'terrible', 'awful', 'love', 'hate']
for keyword in important_keywords:
    df[f'contains_{keyword}'] = df['review'].str.contains(keyword, case=False).astype(int)
\`\`\`

#### Advanced Text Processing
\`\`\`python
from sklearn.feature_extraction.text import TfidfVectorizer
from textblob import TextBlob

# TF-IDF features (most important words)
tfidf = TfidfVectorizer(max_features=100, stop_words='english')
tfidf_features = tfidf.fit_transform(df['review'].fillna(''))
tfidf_df = pd.DataFrame(tfidf_features.toarray(), 
                       columns=[f'tfidf_{word}' for word in tfidf.get_feature_names_out()])

# Sentiment analysis
df['sentiment_polarity'] = df['review'].apply(
    lambda x: TextBlob(x).sentiment.polarity if x else 0
)
df['sentiment_subjectivity'] = df['review'].apply(
    lambda x: TextBlob(x).sentiment.subjectivity if x else 0
)

# Combine all text features
df = pd.concat([df, tfidf_df], axis=1)
\`\`\`

### 5. Interaction Features 🔗

#### Mathematical Interactions
\`\`\`python
# Arithmetic combinations
df['price_per_sqft'] = df['price'] / df['square_feet']
df['income_to_debt_ratio'] = df['income'] / (df['debt'] + 1)  # +1 to avoid division by zero
df['efficiency_score'] = df['output'] / df['input']

# Multiplicative interactions
df['age_income_interaction'] = df['age'] * df['income']
df['size_price_interaction'] = df['size'] * df['price']

# Polynomial features (automated)
from sklearn.preprocessing import PolynomialFeatures

poly = PolynomialFeatures(degree=2, include_bias=False, interaction_only=True)
numerical_cols = ['age', 'income', 'price']
poly_features = poly.fit_transform(df[numerical_cols])
poly_feature_names = poly.get_feature_names_out(numerical_cols)

poly_df = pd.DataFrame(poly_features, columns=poly_feature_names)
df = pd.concat([df, poly_df], axis=1)
\`\`\`

#### Categorical Interactions
\`\`\`python
# Combine categorical variables
df['category_size'] = df['category'] + '_' + df['size']
df['color_brand'] = df['color'] + '_' + df['brand']

# Cross-tabulation features
category_region_avg = df.groupby(['category', 'region'])['price'].transform('mean')
df['category_region_avg_price'] = category_region_avg

# Domain-specific combinations
df['luxury_indicator'] = ((df['price'] > df['price'].quantile(0.8)) & 
                         (df['brand'].isin(['Premium', 'Luxury']))).astype(int)
\`\`\`

## 🎮 Hands-On Exercise: Build a Feature Engineering Pipeline

**Scenario:** You're working with a real estate dataset to predict house prices.

\`\`\`python
# Sample real estate data
import pandas as pd
import numpy as np

# Create sample dataset
np.random.seed(42)
n_houses = 1000

houses = pd.DataFrame({
    'sqft': np.random.normal(2000, 500, n_houses),
    'bedrooms': np.random.randint(1, 6, n_houses),
    'bathrooms': np.random.randint(1, 4, n_houses),
    'age_years': np.random.randint(0, 50, n_houses),
    'neighborhood': np.random.choice(['Downtown', 'Suburban', 'Rural'], n_houses),
    'garage': np.random.choice([0, 1, 2], n_houses),
    'sale_date': pd.date_range('2020-01-01', '2024-01-01', periods=n_houses),
    'price': np.random.normal(300000, 100000, n_houses)
})

print("Original dataset:")
print(houses.head())
print(f"Original features: {houses.shape[1]}")

# TODO: Your feature engineering pipeline
def engineer_real_estate_features(df):
    """
    Create comprehensive features for house price prediction
    """
    df_fe = df.copy()
    
    # TODO 1: Create ratio features
    # Hint: sqft per bedroom, bathrooms per bedroom, etc.
    
    # TODO 2: Encode neighborhood
    # Hint: Use target encoding based on average price
    
    # TODO 3: Extract date features
    # Hint: Year, month, season from sale_date
    
    # TODO 4: Create categorical combinations
    # Hint: Combine neighborhood with other features
    
    # TODO 5: Engineer domain-specific features
    # Hint: House age categories, luxury indicators, etc.
    
    return df_fe

# Apply your feature engineering
houses_engineered = engineer_real_estate_features(houses)
print(f"\\nAfter feature engineering: {houses_engineered.shape[1]} features")
print("New features created:")
print([col for col in houses_engineered.columns if col not in houses.columns])
\`\`\`

### Solution Template:
\`\`\`python
def engineer_real_estate_features(df):
    """Complete feature engineering pipeline for real estate data"""
    df_fe = df.copy()
    
    # 1. Ratio and mathematical features
    df_fe['sqft_per_bedroom'] = df_fe['sqft'] / df_fe['bedrooms']
    df_fe['bathrooms_per_bedroom'] = df_fe['bathrooms'] / df_fe['bedrooms']
    df_fe['total_rooms'] = df_fe['bedrooms'] + df_fe['bathrooms']
    df_fe['sqft_per_room'] = df_fe['sqft'] / df_fe['total_rooms']
    
    # 2. Neighborhood encoding
    neighborhood_avg_price = df_fe.groupby('neighborhood')['price'].transform('mean')
    df_fe['neighborhood_avg_price'] = neighborhood_avg_price
    
    # 3. Date features
    df_fe['sale_year'] = df_fe['sale_date'].dt.year
    df_fe['sale_month'] = df_fe['sale_date'].dt.month
    df_fe['sale_quarter'] = df_fe['sale_date'].dt.quarter
    df_fe['is_summer_sale'] = df_fe['sale_month'].isin([6, 7, 8]).astype(int)
    
    # 4. Age categories
    df_fe['age_category'] = pd.cut(df_fe['age_years'], 
                                  bins=[0, 5, 15, 30, 50], 
                                  labels=['New', 'Recent', 'Mature', 'Old'])
    
    # 5. Luxury indicators
    df_fe['luxury_size'] = (df_fe['sqft'] > df_fe['sqft'].quantile(0.8)).astype(int)
    df_fe['luxury_bathrooms'] = (df_fe['bathrooms'] >= 3).astype(int)
    df_fe['has_garage'] = (df_fe['garage'] > 0).astype(int)
    
    # 6. Neighborhood-size interaction
    df_fe['neighborhood_size'] = df_fe['neighborhood'] + '_' + df_fe['luxury_size'].astype(str)
    
    return df_fe
\`\`\`

## ⚡ Advanced Feature Engineering Techniques

### 1. Automated Feature Engineering
\`\`\`python
# Using featuretools for automated feature engineering
import featuretools as ft

# Define entity set
es = ft.EntitySet()
es = es.add_dataframe(dataframe_name="houses", dataframe=houses, index="house_id")

# Generate features automatically
feature_matrix, feature_defs = ft.dfs(entityset=es, 
                                     target_dataframe_name="houses",
                                     max_depth=2)
print(f"Automated features created: {len(feature_defs)}")
\`\`\`

### 2. Feature Selection After Engineering
\`\`\`python
from sklearn.feature_selection import SelectKBest, f_regression, mutual_info_regression

# Select top features based on statistical tests
selector = SelectKBest(score_func=f_regression, k=20)
selected_features = selector.fit_transform(X_engineered, y)
selected_feature_names = X_engineered.columns[selector.get_support()]

print("Top 20 features by F-test:")
print(selected_feature_names.tolist())

# Mutual information (captures non-linear relationships)
mi_selector = SelectKBest(score_func=mutual_info_regression, k=20)
mi_features = mi_selector.fit_transform(X_engineered, y)
mi_feature_names = X_engineered.columns[mi_selector.get_support()]

print("\\nTop 20 features by mutual information:")
print(mi_feature_names.tolist())
\`\`\`

## ⚠️ Feature Engineering Pitfalls

### 1. Target Leakage ❌
**Problem:** Using future information to predict the past
\`\`\`python
# WRONG: Using future stock price to predict current price
df['tomorrow_price'] = df['price'].shift(-1)  # This is cheating!

# CORRECT: Using historical data
df['yesterday_price'] = df['price'].shift(1)  # This is valid
\`\`\`

### 2. Data Leakage from Test Set ❌
\`\`\`python
# WRONG: Fitting encoders on entire dataset
encoder = StandardScaler()
df['price_scaled'] = encoder.fit_transform(df[['price']])  # Uses test data info!

# CORRECT: Fit only on training data
X_train_scaled = encoder.fit_transform(X_train)
X_test_scaled = encoder.transform(X_test)  # Only transform, don't fit
\`\`\`

### 3. Creating Too Many Features ❌
**Problem:** Curse of dimensionality and overfitting
**Solution:** Use feature selection and regularization

### 4. Not Validating Feature Impact ❌
**Problem:** Assuming more features = better model
**Solution:** Always validate that new features improve performance

## 🏆 Feature Engineering Best Practices

### 1. Start Simple, Then Iterate ✅
1. **Baseline:** Use original features
2. **Enhancement:** Add obvious transformations
3. **Experimentation:** Try advanced techniques
4. **Validation:** Confirm improvements on holdout data

### 2. Domain Knowledge is Key ✅
- Understand your problem domain
- Talk to subject matter experts
- Research how others solve similar problems
- Think about causality, not just correlation

### 3. Document Your Features ✅
\`\`\`python
feature_documentation = {
    'price_per_sqft': 'Price divided by square footage - efficiency metric',
    'age_category': 'House age binned into New/Recent/Mature/Old categories',
    'neighborhood_avg_price': 'Average price in the same neighborhood (target encoded)',
    'luxury_indicator': 'Binary flag: large house (>80th percentile) AND 3+ bathrooms'
}
\`\`\`

### 4. Monitor Feature Performance ✅
\`\`\`python
def evaluate_feature_impact(X_baseline, X_enhanced, y, cv=5):
    """Compare model performance with and without new features"""
    from sklearn.model_selection import cross_val_score
    from sklearn.ensemble import RandomForestRegressor
    
    model = RandomForestRegressor(random_state=42)
    
    baseline_scores = cross_val_score(model, X_baseline, y, cv=cv)
    enhanced_scores = cross_val_score(model, X_enhanced, y, cv=cv)
    
    print(f"Baseline CV Score: {baseline_scores.mean():.3f} (±{baseline_scores.std()*2:.3f})")
    print(f"Enhanced CV Score: {enhanced_scores.mean():.3f} (±{enhanced_scores.std()*2:.3f})")
    print(f"Improvement: {enhanced_scores.mean() - baseline_scores.mean():.3f}")
    
    return enhanced_scores.mean() > baseline_scores.mean()

# Test your feature engineering
improvement = evaluate_feature_impact(X_original, X_engineered, y)
print(f"\\nFeature engineering {'improved' if improvement else 'did not improve'} the model!")
\`\`\`

## 🎯 Key Takeaways

✅ **Feature engineering often trumps algorithm choice** in real-world performance  
✅ **Domain knowledge is your secret weapon** for creating meaningful features  
✅ **Start simple and iterate** - don't engineer everything at once  
✅ **Always validate impact** on holdout data before deploying  
✅ **Document your features** for future team members and debugging  
✅ **Watch for data leakage** - it's the #1 cause of overly optimistic results  
✅ **Less can be more** - 10 great features beat 100 mediocre ones

## 🚀 What's Next?

With strong feature engineering skills, you're now equipped to:
- Transform raw data into ML-ready features
- Improve model performance significantly
- Create domain-specific insights
- Avoid common pitfalls that derail projects

**Remember:** Great feature engineering is part art, part science. The more you practice with different datasets and domains, the better your intuition becomes for what transformations will help your models learn effectively!

*In our final lesson, we'll explore the ethics and responsibility of working with data and AI systems...*`,
    lesson_type: 'text',
    order_index: 6,
    estimated_duration_minutes: 30,
    video_url: null,
    resources: {},
    is_preview: false,
    is_required: true,
    points_value: 25,
    created_at: '2024-02-01T18:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  },
  {
    id: '3-7',
    course_id: '3',
    title: 'Data Ethics and Bias',
    description: 'Learn to build responsible AI systems by understanding and addressing bias in data.',
    content: `# Data Ethics and Bias: Building Responsible AI

As data practitioners, we have immense power to shape how AI systems impact people's lives. With great power comes great responsibility! Let's explore how to build ethical, fair, and inclusive machine learning systems. ⚖️🤖

## 🌍 Why Data Ethics Matter More Than Ever

### The High Stakes of AI Decisions

**Modern AI systems make life-changing decisions:**
- 🏥 **Healthcare:** AI diagnoses diseases and recommends treatments
- 👮 **Criminal Justice:** Algorithms influence bail, sentencing, and parole decisions
- 💼 **Employment:** AI screens resumes and conducts initial interviews
- 🏠 **Housing:** Systems evaluate loan applications and rental prospects
- 🎓 **Education:** Algorithms determine admissions and course placements

**When these systems are biased, real people suffer real consequences.**

### Real-World Examples of AI Bias

#### 1. Hiring Algorithms 💼
**Amazon's Resume Screening (2014-2017):**
- AI system penalized resumes containing words like "women's" (as in "women's chess club captain")
- Downgraded graduates from all-women's colleges
- **Root cause:** Training data from 10 years of male-dominated hiring
- **Impact:** Perpetuated gender bias in tech hiring

#### 2. Criminal Risk Assessment ⚖️
**COMPAS Recidivism Prediction (ProPublica Investigation):**
- Algorithm predicted likelihood of re-offending
- Black defendants labeled high-risk twice as often as white defendants
- White defendants labeled low-risk twice as often as black defendants
- **Same crime rates, different algorithmic treatment**

#### 3. Healthcare AI 🏥
**Hospital Risk Prediction System:**
- Algorithm allocated healthcare resources based on "healthcare costs"
- Systematically underestimated needs of Black patients
- **Why?** Lower spending on Black patients historically created biased training data
- **Result:** Fewer resources allocated to Black patients with same medical needs

#### 4. Facial Recognition Technology 👁️
**IBM, Microsoft, Amazon Systems (MIT Study):**
- Error rates: White men (0.8%) vs Black women (34.7%)
- **Training data problem:** Mostly white, male faces
- **Consequences:** Misidentification in security and law enforcement

## 🔍 Types of Bias in Data and AI

### 1. Historical Bias 📚
**Definition:** Past discrimination embedded in training data
**Example:** Historical hiring data showing male preference in engineering
**Challenge:** "Fair" reflection of biased past vs equitable future

### 2. Representation Bias 📊  
**Definition:** Some groups underrepresented in training data
**Examples:**
- Medical research historically focused on men
- Image datasets with fewer photos of people with darker skin
- Voice recognition trained primarily on certain accents

### 3. Measurement Bias 📏
**Definition:** Different quality/types of data for different groups
**Examples:**
- Wealthier neighborhoods have more sensors and data collection
- Different communities have different access to technology
- Socioeconomic factors affect data completeness

### 4. Aggregation Bias 🎯
**Definition:** Assuming one model fits all subgroups equally well
**Example:** Diabetes risk model trained on general population may not work well for specific ethnic groups with different genetic predispositions

### 5. Evaluation Bias ✅
**Definition:** Using inappropriate benchmarks for model assessment
**Example:** Evaluating language model only on Standard American English when it will be used globally

### 6. Deployment Bias 🚀
**Definition:** Mismatch between model development and real-world usage
**Example:** Skin cancer detection AI trained on dermatology clinic photos used for general population screening

## 🛠️ Detecting Bias in Your Data

### 1. Demographic Parity Analysis
\`\`\`python
import pandas as pd
import numpy as np
import matplotlib.pyplot as plt
import seaborn as sns

def analyze_demographic_parity(df, protected_attribute, outcome, positive_outcome=1):
    """
    Check if outcomes are equally distributed across demographic groups
    """
    print(f"=== DEMOGRAPHIC PARITY ANALYSIS ===")
    print(f"Protected Attribute: {protected_attribute}")
    print(f"Outcome: {outcome}")
    print(f"Positive Outcome: {positive_outcome}")
    
    # Calculate group-specific rates
    group_stats = df.groupby(protected_attribute)[outcome].agg([
        'count', 
        'mean', 
        lambda x: (x == positive_outcome).sum()
    ]).round(3)
    
    group_stats.columns = ['Total', 'Positive_Rate', 'Positive_Count']
    print(f"\\nGroup Statistics:")
    print(group_stats)
    
    # Calculate disparate impact ratio
    rates = group_stats['Positive_Rate']
    max_rate = rates.max()
    min_rate = rates.min()
    
    disparate_impact_ratio = min_rate / max_rate
    print(f"\\nDisparate Impact Ratio: {disparate_impact_ratio:.3f}")
    print(f"(80% rule threshold: 0.8)")
    
    if disparate_impact_ratio < 0.8:
        print("⚠️  POTENTIAL BIAS DETECTED - Significant disparity between groups")
    else:
        print("✅ BIAS CHECK PASSED - Groups have similar outcomes")
    
    # Visualization
    plt.figure(figsize=(10, 6))
    
    plt.subplot(1, 2, 1)
    group_stats['Positive_Rate'].plot(kind='bar', color='skyblue', edgecolor='black')
    plt.title(f'Positive Rate by {protected_attribute}')
    plt.ylabel('Positive Rate')
    plt.axhline(y=0.8*max_rate, color='red', linestyle='--', label='80% of max rate')
    plt.legend()
    plt.xticks(rotation=45)
    
    plt.subplot(1, 2, 2)
    group_stats['Total'].plot(kind='bar', color='lightgreen', edgecolor='black')
    plt.title(f'Sample Size by {protected_attribute}')
    plt.ylabel('Count')
    plt.xticks(rotation=45)
    
    plt.tight_layout()
    plt.show()
    
    return group_stats, disparate_impact_ratio

# Example usage
sample_data = pd.DataFrame({
    'gender': ['M', 'F'] * 500,
    'race': np.random.choice(['White', 'Black', 'Hispanic', 'Asian'], 1000),
    'age': np.random.randint(18, 65, 1000),
    'loan_approved': np.random.randint(0, 2, 1000)
})

stats, ratio = analyze_demographic_parity(sample_data, 'gender', 'loan_approved')
\`\`\`

### 2. Intersectional Bias Analysis
\`\`\`python
def analyze_intersectional_bias(df, protected_attributes, outcome):
    """
    Check bias across intersecting demographic categories
    """
    print(f"=== INTERSECTIONAL BIAS ANALYSIS ===")
    
    # Create intersectional groups
    df['intersect_group'] = df[protected_attributes].apply(
        lambda x: '_'.join(x.astype(str)), axis=1
    )
    
    # Analyze each intersection
    intersect_stats = df.groupby('intersect_group')[outcome].agg([
        'count', 'mean'
    ]).round(3)
    
    intersect_stats.columns = ['Count', 'Positive_Rate']
    intersect_stats = intersect_stats.sort_values('Positive_Rate', ascending=False)
    
    print("Intersectional Group Analysis:")
    print(intersect_stats)
    
    # Find largest disparities
    max_rate = intersect_stats['Positive_Rate'].max()
    min_rate = intersect_stats['Positive_Rate'].min()
    
    print(f"\\nLargest Disparity:")
    print(f"Highest rate: {intersect_stats['Positive_Rate'].idxmax()} ({max_rate:.3f})")
    print(f"Lowest rate: {intersect_stats['Positive_Rate'].idxmin()} ({min_rate:.3f})")
    print(f"Ratio: {min_rate/max_rate:.3f}")
    
    # Visualize
    plt.figure(figsize=(12, 6))
    intersect_stats['Positive_Rate'].plot(kind='bar', color='coral', edgecolor='black')
    plt.title('Positive Rates by Intersectional Groups')
    plt.ylabel('Positive Rate')
    plt.xticks(rotation=45)
    plt.tight_layout()
    plt.show()
    
    return intersect_stats

# Example usage
intersect_analysis = analyze_intersectional_bias(
    sample_data, ['gender', 'race'], 'loan_approved'
)
\`\`\`

### 3. Feature Correlation with Protected Attributes
\`\`\`python
def check_feature_correlations(df, protected_attributes, features):
    """
    Identify features that might serve as proxies for protected attributes
    """
    print("=== PROXY VARIABLE ANALYSIS ===")
    
    correlations = {}
    
    for protected_attr in protected_attributes:
        if df[protected_attr].dtype == 'object':
            # For categorical protected attributes, use target encoding
            encoded = pd.get_dummies(df[protected_attr], prefix=protected_attr)
            attr_corr = {}
            
            for feature in features:
                if feature in df.columns and pd.api.types.is_numeric_dtype(df[feature]):
                    # Calculate correlation with each category
                    for encoded_col in encoded.columns:
                        corr = df[feature].corr(encoded[encoded_col])
                        if abs(corr) > 0.1:  # Threshold for concerning correlation
                            attr_corr[f"{feature}_vs_{encoded_col}"] = corr
            
            correlations[protected_attr] = attr_corr
        else:
            # For numerical protected attributes
            attr_corr = {}
            for feature in features:
                if feature in df.columns and pd.api.types.is_numeric_dtype(df[feature]):
                    corr = df[feature].corr(df[protected_attr])
                    if abs(corr) > 0.1:
                        attr_corr[feature] = corr
            
            correlations[protected_attr] = attr_corr
    
    # Report potential proxies
    for attr, corr_dict in correlations.items():
        if corr_dict:
            print(f"\\nPotential proxies for {attr}:")
            for feature, corr in sorted(corr_dict.items(), key=lambda x: abs(x[1]), reverse=True):
                print(f"  {feature}: {corr:.3f}")
        else:
            print(f"\\n✅ No concerning correlations found for {attr}")
    
    return correlations

# Check for proxy variables
proxy_check = check_feature_correlations(
    sample_data, ['gender'], ['age', 'loan_approved']
)
\`\`\`

## 🛡️ Bias Mitigation Techniques

### 1. Data-Level Interventions

#### Balanced Sampling
\`\`\`python
def create_balanced_dataset(df, protected_attribute, target, method='undersample'):
    """
    Balance dataset across protected attribute groups
    """
    groups = df.groupby(protected_attribute)
    
    if method == 'undersample':
        # Sample to smallest group size
        min_size = groups.size().min()
        balanced_df = groups.apply(lambda x: x.sample(min_size, random_state=42))
        
    elif method == 'oversample':
        # Sample to largest group size  
        max_size = groups.size().max()
        balanced_df = groups.apply(
            lambda x: x.sample(max_size, replace=True, random_state=42)
        )
    
    elif method == 'smote':
        # Synthetic Minority Oversampling Technique
        from imblearn.over_sampling import SMOTE
        
        # Encode categorical variables first
        df_encoded = pd.get_dummies(df, columns=[protected_attribute])
        
        smote = SMOTE(random_state=42)
        X_resampled, y_resampled = smote.fit_resample(
            df_encoded.drop(target, axis=1), 
            df_encoded[target]
        )
        
        balanced_df = pd.concat([X_resampled, y_resampled], axis=1)
    
    return balanced_df.reset_index(drop=True)

# Example usage
balanced_data = create_balanced_dataset(sample_data, 'gender', 'loan_approved', method='oversample')
print(f"Original distribution:\\n{sample_data['gender'].value_counts()}")
print(f"\\nBalanced distribution:\\n{balanced_data['gender'].value_counts()}")
\`\`\`

#### Data Augmentation for Underrepresented Groups
\`\`\`python
def augment_underrepresented_groups(df, protected_attribute, min_representation=0.2):
    """
    Add synthetic data for underrepresented groups
    """
    group_proportions = df[protected_attribute].value_counts(normalize=True)
    underrepresented = group_proportions[group_proportions < min_representation]
    
    print(f"Underrepresented groups (< {min_representation*100}%):")
    print(underrepresented)
    
    # Generate synthetic samples for underrepresented groups
    # This is a simplified example - in practice, use more sophisticated methods
    augmented_df = df.copy()
    
    for group in underrepresented.index:
        group_data = df[df[protected_attribute] == group]
        current_count = len(group_data)
        target_count = int(len(df) * min_representation)
        additional_needed = max(0, target_count - current_count)
        
        if additional_needed > 0:
            # Simple duplication with noise (use better methods in practice)
            additional_samples = group_data.sample(
                additional_needed, 
                replace=True, 
                random_state=42
            ).copy()
            
            # Add small amount of noise to numerical columns
            numeric_cols = additional_samples.select_dtypes(include=[np.number]).columns
            for col in numeric_cols:
                if col != protected_attribute:
                    noise = np.random.normal(0, additional_samples[col].std() * 0.1, len(additional_samples))
                    additional_samples[col] += noise
            
            augmented_df = pd.concat([augmented_df, additional_samples], ignore_index=True)
            print(f"Added {additional_needed} samples for {group}")
    
    return augmented_df
\`\`\`

### 2. Algorithm-Level Interventions

#### Fairness-Aware Machine Learning
\`\`\`python
from sklearn.linear_model import LogisticRegression
from sklearn.metrics import accuracy_score, confusion_matrix

class FairnessAwareModel:
    """
    Model wrapper that optimizes for both accuracy and fairness
    """
    
    def __init__(self, base_model=LogisticRegression(), fairness_weight=0.5):
        self.base_model = base_model
        self.fairness_weight = fairness_weight
        
    def fit(self, X, y, protected_attribute):
        """
        Train model with fairness constraints
        """
        # Train base model
        self.base_model.fit(X, y)
        
        # Store for fairness evaluation
        self.protected_attribute = protected_attribute
        
        return self
    
    def predict(self, X):
        return self.base_model.predict(X)
    
    def predict_proba(self, X):
        return self.base_model.predict_proba(X)
    
    def evaluate_fairness(self, X, y, protected_attr_values):
        """
        Evaluate model fairness across groups
        """
        predictions = self.predict(X)
        
        fairness_metrics = {}
        
        for group in np.unique(protected_attr_values):
            group_mask = protected_attr_values == group
            group_accuracy = accuracy_score(y[group_mask], predictions[group_mask])
            
            # True Positive Rate (Sensitivity)
            tn, fp, fn, tp = confusion_matrix(
                y[group_mask], predictions[group_mask]
            ).ravel()
            
            tpr = tp / (tp + fn) if (tp + fn) > 0 else 0
            fpr = fp / (fp + tn) if (fp + tn) > 0 else 0
            
            fairness_metrics[group] = {
                'accuracy': group_accuracy,
                'true_positive_rate': tpr,
                'false_positive_rate': fpr
            }
        
        return fairness_metrics

# Example usage
fair_model = FairnessAwareModel()
X = sample_data[['age']].values  # Simple example with just age
y = sample_data['loan_approved'].values
protected_attr = sample_data['gender'].values

fair_model.fit(X, y, protected_attr)
fairness_results = fair_model.evaluate_fairness(X, y, protected_attr)

print("Fairness Evaluation Results:")
for group, metrics in fairness_results.items():
    print(f"\\n{group}:")
    for metric, value in metrics.items():
        print(f"  {metric}: {value:.3f}")
\`\`\`

### 3. Post-Processing Interventions

#### Threshold Optimization for Fairness
\`\`\`python
def optimize_fairness_thresholds(model, X, y, protected_attr, fairness_metric='equalized_odds'):
    """
    Find optimal prediction thresholds for each group to ensure fairness
    """
    from sklearn.metrics import roc_curve
    
    # Get prediction probabilities
    y_prob = model.predict_proba(X)[:, 1]
    
    # Find optimal thresholds for each group
    thresholds = {}
    
    for group in np.unique(protected_attr):
        group_mask = protected_attr == group
        fpr, tpr, thresh = roc_curve(y[group_mask], y_prob[group_mask])
        
        if fairness_metric == 'equalized_odds':
            # Optimize for equal TPR across groups
            target_tpr = 0.8  # or use average TPR across all groups
            
            # Find threshold closest to target TPR
            optimal_idx = np.argmin(np.abs(tpr - target_tpr))
            thresholds[group] = thresh[optimal_idx]
        
        elif fairness_metric == 'demographic_parity':
            # Optimize for equal positive prediction rates
            target_positive_rate = np.mean(y)  # Overall positive rate
            
            # Find threshold that gives closest to target rate
            best_threshold = 0.5
            best_diff = float('inf')
            
            for threshold in np.linspace(0.1, 0.9, 100):
                pred_rate = np.mean(y_prob[group_mask] >= threshold)
                diff = abs(pred_rate - target_positive_rate)
                
                if diff < best_diff:
                    best_diff = diff
                    best_threshold = threshold
            
            thresholds[group] = best_threshold
    
    print("Optimal Thresholds for Fairness:")
    for group, threshold in thresholds.items():
        print(f"  {group}: {threshold:.3f}")
    
    return thresholds

def apply_fair_thresholds(y_prob, protected_attr, thresholds):
    """
    Apply group-specific thresholds for fair predictions
    """
    fair_predictions = np.zeros_like(y_prob, dtype=int)
    
    for group, threshold in thresholds.items():
        group_mask = protected_attr == group
        fair_predictions[group_mask] = (y_prob[group_mask] >= threshold).astype(int)
    
    return fair_predictions

# Example usage
from sklearn.ensemble import RandomForestClassifier

# Train a model
rf_model = RandomForestClassifier(random_state=42)
rf_model.fit(X, y)

# Optimize thresholds for fairness
fair_thresholds = optimize_fairness_thresholds(
    rf_model, X, y, protected_attr, fairness_metric='demographic_parity'
)

# Apply fair thresholds
y_prob = rf_model.predict_proba(X)[:, 1]
fair_predictions = apply_fair_thresholds(y_prob, protected_attr, fair_thresholds)

# Compare fairness before and after
print("\\nBefore threshold optimization:")
original_stats, _ = analyze_demographic_parity(
    pd.DataFrame({'gender': protected_attr, 'prediction': rf_model.predict(X)}),
    'gender', 'prediction'
)

print("\\nAfter threshold optimization:")
fair_stats, _ = analyze_demographic_parity(
    pd.DataFrame({'gender': protected_attr, 'prediction': fair_predictions}),
    'gender', 'prediction'
)
\`\`\`

## 🎓 Quiz: Data Ethics and Bias

Test your understanding with these scenarios!

### Question 1: Identifying Bias Types
**Scenario:** A medical AI trained on data from a prestigious hospital (mostly wealthy, white patients) is deployed in community clinics serving diverse populations.

**What types of bias are present?**
A) Historical bias only
B) Representation bias only  
C) Both representation and deployment bias
D) Measurement bias only

<details>
<summary>Click for answer</summary>
**Answer: C) Both representation and deployment bias**

- **Representation bias:** Training data doesn't represent the deployment population
- **Deployment bias:** Mismatch between training environment (prestigious hospital) and deployment environment (community clinics)
</details>

### Question 2: Fairness Metrics
**Scenario:** A hiring algorithm has these results:
- Men: 80% hired (800 out of 1000 applicants)
- Women: 60% hired (600 out of 1000 applicants)

**Questions:**
1. What's the disparate impact ratio?
2. Does this pass the "80% rule"?
3. What additional information would you need to fully assess fairness?

<details>
<summary>Click for answers</summary>
**Answers:**
1. **Disparate impact ratio:** 60% ÷ 80% = 0.75
2. **80% rule:** No, fails (0.75 < 0.8)  
3. **Additional info needed:**
   - Qualification distributions by gender
   - Interview performance data
   - Historical hiring patterns
   - Job requirements and relevance of criteria
</details>

### Question 3: Mitigation Strategy
**Scenario:** You discover your loan approval model has 20% approval rate for one ethnic group and 40% for another.

**Which mitigation approach would you try first?**
A) Use different approval thresholds for each group
B) Collect more data for the underrepresented group
C) Investigate the root cause of the disparity
D) Remove race from the dataset

<details>
<summary>Click for answer</summary>
**Answer: C) Investigate the root cause of the disparity**

**Reasoning:**
- First understand WHY the disparity exists
- Could be data quality issues, proxy variables, or legitimate risk factors
- Other approaches might address symptoms but not root causes
- Removing race doesn't eliminate proxy variables
</details>

### Question 4: Proxy Variables
**Which of these features could serve as proxies for race in a US context?**
A) ZIP code
B) Name  
C) Shopping patterns
D) All of the above

<details>
<summary>Click for answer</summary>
**Answer: D) All of the above**

**Explanations:**
- **ZIP code:** Residential segregation makes this highly predictive of race
- **Name:** Certain names are strongly associated with ethnic backgrounds
- **Shopping patterns:** Cultural preferences and economic factors create patterns
- **Key insight:** Removing protected attributes isn't enough - proxies can perpetuate bias
</details>

### Question 5: Ethical Decision Making
**Your fairness intervention reduces overall model accuracy from 85% to 82% but makes outcomes equal across groups.**

**What should you do?**
A) Keep the original model - accuracy is most important
B) Use the fair model - equity matters more than small accuracy gains
C) It depends on the application context and stakeholder values
D) Split the difference with a compromise solution

<details>
<summary>Click for answer</summary>
**Answer: C) It depends on the application context and stakeholder values**

**Considerations:**
- **High stakes decisions** (loans, healthcare): Fairness often more important
- **Commercial applications**: Business might prioritize accuracy
- **Stakeholder involvement**: Include affected communities in decision
- **Regulatory requirements**: Some domains require fairness by law
- **Long-term consequences:** Biased systems can cause lasting harm
</details>

## 🎯 Building Ethical AI: Your Action Plan

### 1. Before You Start 🔍
- [ ] **Understand your domain:** What are the fairness concerns?
- [ ] **Identify stakeholders:** Who is affected by your system?
- [ ] **Review regulations:** Are there legal requirements for fairness?
- [ ] **Set ethical guidelines:** What values will guide your decisions?

### 2. During Data Collection 📊
- [ ] **Audit your data sources:** Are all groups represented?
- [ ] **Document collection methods:** How might this introduce bias?
- [ ] **Check for historical bias:** Does past data reflect unfair practices?
- [ ] **Consider data quality:** Is data quality consistent across groups?

### 3. During Model Development 🛠️
- [ ] **Test for bias early and often:** Use the techniques from this lesson
- [ ] **Consider multiple fairness metrics:** No single metric captures everything
- [ ] **Validate on diverse populations:** Test on different subgroups
- [ ] **Document trade-offs:** Be transparent about accuracy vs fairness

### 4. Before Deployment 🚀
- [ ] **Conduct bias testing:** Final check with deployment-similar data
- [ ] **Plan monitoring:** How will you detect bias in production?
- [ ] **Prepare mitigation:** Have bias correction procedures ready
- [ ] **Train users:** Educate people using the system about limitations

### 5. After Deployment 📈
- [ ] **Monitor continuously:** Bias can emerge over time
- [ ] **Collect feedback:** Listen to affected communities
- [ ] **Update regularly:** Retrain models as data and context change
- [ ] **Document learnings:** Share insights with the broader community

## 🌟 Key Takeaways

✅ **Bias is not just a technical problem** - it's a social justice issue with real consequences  
✅ **Fairness is not automatic** - ML systems often amplify existing biases  
✅ **Multiple perspectives matter** - include diverse voices in development  
✅ **There's no perfect solution** - fairness often requires trade-offs  
✅ **Continuous vigilance is required** - bias can emerge post-deployment  
✅ **Documentation and transparency are essential** - others need to understand your decisions  

## 🚀 Your Responsibility as a Data Professional

As you begin your journey in machine learning, remember:

- **Your code affects real people** - take that responsibility seriously
- **Bias is everyone's problem** - not just the responsibility of underrepresented groups
- **Perfect is the enemy of good** - start with basic fairness measures and iterate
- **Stay educated** - the field of AI ethics is rapidly evolving
- **Speak up** - advocate for fairness even when it's uncomfortable

## 📚 Resources for Continued Learning

### Books
- "Weapons of Math Destruction" by Cathy O'Neil
- "Race After Technology" by Ruha Benjamin  
- "Algorithms of Oppression" by Safiya Noble

### Tools & Frameworks
- **IBM AI Fairness 360:** Open-source toolkit for bias detection and mitigation
- **Google What-If Tool:** Interactive tool for probing ML models
- **Microsoft Fairlearn:** Python package for fairness assessment and improvement

### Organizations & Communities  
- **Partnership on AI:** Cross-industry organization focused on AI best practices
- **AI Now Institute:** Research institute studying AI's social implications  
- **Black in AI, Queer in AI, LatinX in AI:** Communities promoting inclusion in AI

## 🏆 Congratulations!

You've completed your journey through Data Fundamentals for ML! 🎉

**You now understand:**
- ✅ What makes data good vs problematic
- ✅ How to find and evaluate data sources
- ✅ Different data types and their ML implications  
- ✅ Techniques for cleaning messy, real-world data
- ✅ Visual exploration methods to uncover insights
- ✅ Feature engineering to improve model performance
- ✅ Ethical considerations and bias mitigation

**Most importantly:** You're equipped to work with data responsibly and build AI systems that help rather than harm.

**Keep learning, keep questioning, and keep building a better future with AI!** 🌟

*Your data journey continues - every dataset is a new opportunity to learn and make a positive impact.*`,
    lesson_type: 'quiz',
    order_index: 7,
    estimated_duration_minutes: 25,
    video_url: null,
    resources: {
      questions: [
        {
          question: "A medical AI trained on data from prestigious hospitals (mostly wealthy, white patients) is deployed in community clinics. What types of bias are present?",
          options: [
            "Historical bias only",
            "Representation bias only", 
            "Both representation and deployment bias",
            "Measurement bias only"
          ],
          correctAnswer: 2,
          explanation: "This scenario shows representation bias (training data doesn't represent deployment population) and deployment bias (mismatch between training and deployment environments)."
        },
        {
          question: "A hiring algorithm has 80% hire rate for men and 60% for women. What's the disparate impact ratio?",
          options: [
            "0.60",
            "0.75", 
            "0.80",
            "1.33"
          ],
          correctAnswer: 1,
          explanation: "Disparate impact ratio = 60% ÷ 80% = 0.75. This fails the '80% rule' (0.75 < 0.8), indicating potential bias."
        },
        {
          question: "Which feature could serve as a proxy for race in US loan applications?",
          options: [
            "ZIP code only",
            "First and last name only",
            "Shopping patterns only", 
            "All of the above"
          ],
          correctAnswer: 3,
          explanation: "All can be proxies due to residential segregation (ZIP), naming patterns, and cultural/economic factors affecting shopping. Removing protected attributes isn't enough - proxies can perpetuate bias."
        },
        {
          question: "Your fairness intervention reduces accuracy from 85% to 82% but equalizes outcomes. What should you do?",
          options: [
            "Keep the original model - accuracy is most important",
            "Use the fair model - equity always matters more",
            "It depends on the application context and stakeholder values",
            "Always split the difference with a compromise"
          ],
          correctAnswer: 2,
          explanation: "The right choice depends on context: high-stakes decisions often prioritize fairness, while business applications might favor accuracy. Stakeholder involvement and regulatory requirements matter."
        },
        {
          question: "What's the most important first step when discovering bias in your model?",
          options: [
            "Use different thresholds for each group",
            "Collect more data for underrepresented groups",
            "Investigate the root cause of the disparity", 
            "Remove protected attributes from the dataset"
          ],
          correctAnswer: 2,
          explanation: "Understanding WHY disparity exists is crucial before choosing mitigation strategies. Could be data quality, proxy variables, or legitimate factors. Other approaches might treat symptoms, not causes."
        }
      ]
    },
    is_preview: false,
    is_required: true,
    points_value: 22,
    created_at: '2024-02-01T19:30:00Z',
    updated_at: '2024-03-12T10:45:00Z'
  }
];

// Additional mock data for instructor info, reviews, etc.
export const MOCK_INSTRUCTORS = {
  '1': {
    id: 'inst-1',
    name: 'Dr. Sarah Chen',
    title: 'AI Research Scientist',
    bio: 'Former Google AI researcher with 8 years experience teaching machine learning. PhD in Computer Science from Stanford.',
    avatar: null,
    expertise: ['Machine Learning', 'Deep Learning', 'Computer Vision'],
    courses_taught: 15,
    student_count: 12500,
    rating: 4.9
  },
  '2': {
    id: 'inst-2', 
    name: 'Marcus Rodriguez',
    title: 'Senior Data Scientist',
    bio: 'Netflix data scientist specializing in recommendation systems. Passionate about making ML accessible to everyone.',
    avatar: null,
    expertise: ['Recommendation Systems', 'Data Mining', 'Python'],
    courses_taught: 8,
    student_count: 8900,
    rating: 4.8
  },
  '3': {
    id: 'inst-3',
    name: 'Dr. Emily Watson',
    title: 'Data Engineering Consultant',
    bio: 'Former Spotify engineer who built large-scale data pipelines. Expert in making data ML-ready.',
    avatar: null,
    expertise: ['Data Engineering', 'SQL', 'Data Visualization'],
    courses_taught: 12,
    student_count: 6700,
    rating: 4.7
  }
};

export const MOCK_REVIEWS = {
  '1': [
    {
      id: 'rev-1-1',
      user_name: 'Alex Kim',
      rating: 5,
      comment: 'Perfect introduction to ML! The explanations are clear and the examples really help you understand the concepts. Highly recommended for beginners.',
      date: '2024-03-15',
      helpful_count: 24
    },
    {
      id: 'rev-1-2',  
      user_name: 'Jennifer L.',
      rating: 5,
      comment: 'I had zero programming experience and this course made ML approachable. The interactive exercises are fantastic!',
      date: '2024-03-10',
      helpful_count: 18
    },
    {
      id: 'rev-1-3',
      user_name: 'David Chen',
      rating: 4,
      comment: 'Great content but could use more coding examples. Still, excellent for understanding the fundamentals.',
      date: '2024-03-05',
      helpful_count: 12
    },
    {
      id: 'rev-1-4',
      user_name: 'Maria Santos',
      rating: 5,
      comment: 'The real-world examples really clicked for me. Now I understand how Netflix recommendations work!',
      date: '2024-02-28',
      helpful_count: 31
    }
  ],
  '2': [
    {
      id: 'rev-2-1',
      user_name: 'Tom Wilson',
      rating: 5,
      comment: 'Finally built my first ML model! The step-by-step approach made it less intimidating than I expected.',
      date: '2024-03-12',
      helpful_count: 19
    },
    {
      id: 'rev-2-2',
      user_name: 'Lisa Park',
      rating: 4,
      comment: 'Good hands-on course. Some parts moved a bit fast for me but the replay feature helped.',
      date: '2024-03-08',
      helpful_count: 8
    },
    {
      id: 'rev-2-3',
      user_name: 'James Wright',
      rating: 5,
      comment: 'The movie recommendation project was so cool! I actually use my model to suggest movies to friends.',
      date: '2024-03-01',
      helpful_count: 22
    }
  ],
  '3': [
    {
      id: 'rev-3-1', 
      user_name: 'Anna Bell',
      rating: 4,
      comment: 'Comprehensive coverage of data fundamentals. The data cleaning section was especially valuable.',
      date: '2024-03-14',
      helpful_count: 15
    },
    {
      id: 'rev-3-2',
      user_name: 'Robert Kim',
      rating: 5,
      comment: 'This course saved me so much time on my first ML project. Wish I had taken it earlier!',
      date: '2024-03-09',
      helpful_count: 20
    },
    {
      id: 'rev-3-3',
      user_name: 'Sophie Chen',
      rating: 4,
      comment: 'Great practical examples. The ethics section really made me think about responsible AI.',
      date: '2024-02-25',
      helpful_count: 13
    }
  ]
};